import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import toast from "react-hot-toast";
import { useThemeMode } from "../theme/ThemeContext";
import {
  getPlayersRelatedToTeam,
  saveLineupBulk,
  searchPlayerGlobal,
  addPlayersToTeam,
  portPlayerToOrganizer,
  createUser,
  requestDuplicatePlayerApproval,
  raiseDuplicatePlayerFlag,
  isCreatableDuplicate,
  DUPLICATE_TYPE,
} from "../config/mainRequest";
import {
  Box,
  Stack,
  Typography,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Chip,
  Autocomplete,
  useMediaQuery,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import {
  Add,
  Close,
  Edit,
  Delete,
  Save,
  SwapHoriz,
  PersonAdd,
  Download,
} from "@mui/icons-material";
import { FaFilePdf, FaFileAlt, FaDownload, FaCopy } from "react-icons/fa";
import PlayerProfileForm, {
  fieldSx,
  LabeledField,
  CustomSelect,
  POSITIONS,
  EMPTY_PLAYER_FORM,
} from "./PlayerProfileForm";
import PlayerMetaLine, { jerseyNumberOf } from "./PlayerMetaLine";
import { positionAlias } from "../constant/positionList";

// A global search returns one row per team membership, so the same player id
// can appear several times across teams/organizers. Collapse to one row per
// player — a repeated id is the SAME person, never a port candidate twice.
// Null-safe id comparison. Plain String(a) === String(b) is a trap here:
// two missing ids both stringify to "undefined" and compare EQUAL, which made
// every id-less search row look like a player already in the squad.
const sameId = (a, b) => a != null && b != null && String(a) === String(b);

const groupDuplicateRows = (rows = []) => {
  const byId = new Map();
  rows.forEach((r) => {
    if (!r) return;
    const key = String(r.id ?? `${r.first_name || ""}-${r.last_name || ""}`);
    const g = byId.get(key) || {
      id: r.id,
      name: `${r.first_name || ""} ${r.last_name || ""}`.trim() || r.name || `#${r.id}`,
      // team↔organizer kept as PAIRS, never two flat lists — the row must be
      // able to show only the membership that the chosen action is about.
      memberships: [],
      fuzz: null,
      jersey: null,
      position: "",
    };
    const dupe = g.memberships.some(
      (m) =>
        String(m.teamId ?? "") === String(r.team_id ?? "") &&
        String(m.organizerId ?? "") === String(r.organizer_id ?? "")
    );
    if (!dupe && (r.team_id != null || r.organizer_id != null)) {
      g.memberships.push({
        teamId: r.team_id ?? null,
        teamName: r.team_name || (r.team_id != null ? `#${r.team_id}` : ""),
        organizerId: r.organizer_id ?? null,
        organizerName: r.organizer_name || (r.organizer_id != null ? `#${r.organizer_id}` : ""),
      });
    }
    if (r.fuzz_ratio != null) g.fuzz = Math.max(g.fuzz ?? 0, r.fuzz_ratio);
    if (g.jersey == null) g.jersey = jerseyNumberOf(r);
    if (!g.position) g.position = positionAlias(r.preferred_position);
    byId.set(key, g);
  });
  return Array.from(byId.values());
};

/** Distinct, non-empty values of one membership field. */
const uniqueField = (memberships, field) => [
  ...new Set(memberships.map((m) => m[field]).filter(Boolean)),
];

const HOME_COLOR = "#1C64F2";
const AWAY_COLOR = "#F97316";

// ─── Field Slot Definitions ───
// Derived from the TMS positionMap grid (home = cols 0-8, rows 0-6):
//   x = (col + 0.5) / 18 * 100   y = 11 + row/6 * 78  (padded so top/bottom
//   rows stay inside the pitch instead of clipping the border).
// Home attacks left→right; the away side is a mirror of this grid.
const HOME_FIELD_SLOTS = [
  { id: "h_GK", pos: "GK", x: 2.8, y: 50 },
  // Back line (col 1) — full-backs in line with the centre-backs
  { id: "h_LB", pos: "LB", x: 8.3, y: 11 },
  { id: "h_CB_0", pos: "CB", x: 8.3, y: 37 },
  { id: "h_CB_1", pos: "CB", x: 8.3, y: 50 },
  { id: "h_CB_2", pos: "CB", x: 8.3, y: 63 },
  { id: "h_RB", pos: "RB", x: 8.3, y: 89 },
  // Wing-backs (col 2)
  { id: "h_LWB", pos: "LWB", x: 13.9, y: 11 },
  { id: "h_RWB", pos: "RWB", x: 13.9, y: 89 },
  // Defensive midfield (col 3)
  { id: "h_CDM_0", pos: "CDM", x: 19.4, y: 37 },
  { id: "h_CDM_1", pos: "CDM", x: 19.4, y: 50 },
  { id: "h_CDM_2", pos: "CDM", x: 19.4, y: 63 },
  // Midfield line (col 4) — wide mids in line with the central mids
  { id: "h_LM", pos: "LM", x: 25, y: 11 },
  { id: "h_CM_0", pos: "CM", x: 25, y: 37 },
  { id: "h_CM_1", pos: "CM", x: 25, y: 50 },
  { id: "h_CM_2", pos: "CM", x: 25, y: 63 },
  { id: "h_RM", pos: "RM", x: 25, y: 89 },
  // Attacking midfield (col 5)
  { id: "h_CAM_0", pos: "CAM", x: 30.6, y: 37 },
  { id: "h_CAM_1", pos: "CAM", x: 30.6, y: 50 },
  { id: "h_CAM_2", pos: "CAM", x: 30.6, y: 63 },
  // Wingers (col 6)
  { id: "h_LW", pos: "LW", x: 36.1, y: 11 },
  { id: "h_RW", pos: "RW", x: 36.1, y: 89 },
  // Forwards (col 7)
  { id: "h_LF", pos: "LF", x: 41.7, y: 37 },
  { id: "h_CF", pos: "CF", x: 41.7, y: 50 },
  { id: "h_RF", pos: "RF", x: 41.7, y: 63 },
  // Striker (col 8)
  { id: "h_ST", pos: "ST", x: 47.2, y: 50 },
];

// Away mirrors home across both axes.
const AWAY_FIELD_SLOTS = HOME_FIELD_SLOTS.map((s) => ({
  id: s.id.replace("h_", "a_"),
  pos: s.pos,
  x: 100 - s.x,
  y: 100 - s.y,
}));

const ALL_HOME_SLOTS = HOME_FIELD_SLOTS;
const ALL_AWAY_SLOTS = AWAY_FIELD_SLOTS;
const ALL_SLOTS = [...ALL_HOME_SLOTS, ...ALL_AWAY_SLOTS];

// Match statuses where players are considered to have played in the match.
const PLAYED_IN_STATUSES = [3, 6, 7, 8, 9];

// Map an API player object (spec §1) → the squad item shape this UI uses.
// preferred_position comes back as a numeric id (1-17); map it to its alias.
const POS_ALIAS_BY_ID = {
  1: "GK", 2: "CB", 3: "LB", 4: "RB", 5: "LWB", 6: "RWB", 7: "CDM", 8: "CM",
  9: "CAM", 10: "LM", 11: "RM", 12: "LW", 13: "RW", 14: "LF", 15: "RF",
  16: "CF", 17: "ST",
};
const posAlias = (v) => {
  if (v == null || v === "") return "";
  return POS_ALIAS_BY_ID[Number(v)] || String(v);
};
const POS_ID_BY_ALIAS = Object.entries(POS_ALIAS_BY_ID).reduce((m, [id, a]) => {
  m[a] = Number(id);
  return m;
}, {});
// alias/id → numeric position id for the save payload.
const posId = (v) => {
  if (v == null || v === "") return null;
  if (POS_ID_BY_ALIAS[v]) return POS_ID_BY_ALIAS[v];
  const n = Number(v);
  return POS_ALIAS_BY_ID[n] ? n : null;
};

// preferred_position_sub_level convention: 0 = middle, 1 = top, 2 = bottom.
// `group` = same-position slots sorted top→bottom.
const slotForSubLevel = (group, lvl) => {
  const n = group.length;
  if (!n) return null;
  const L = Number(lvl);
  if (!Number.isFinite(L)) return null;
  if (L === 1) return group[0]; // top
  if (L === 2) return group[n - 1]; // bottom
  if (L === 0) return group[Math.floor((n - 1) / 2)]; // middle
  return group[L] || null;
};
const subLevelForSlotIndex = (idx, n) => {
  if (n <= 1) return 0; // single slot → middle
  if (idx === 0) return 1; // top
  if (idx === n - 1) return 2; // bottom
  return 0; // middle
};

const mapApiPlayer = (p) => {
  const umd = p.user_match_details || {};
  return {
    id: p.id,
    name: `${p.first_name || ""} ${p.last_name || ""}`.trim() || `#${umd.jersey_number ?? ""}`,
    num: umd.jersey_number ?? "",
    pos: posAlias(umd.preferred_position),
    posSub: umd.preferred_position_sub_level ?? null,
    umd,
    raw: p,
  };
};

// Labelled detail for a squad player in the "Player Name" pickers. Optional
// fields are read defensively — PlayerMetaLine drops whatever is absent.
const squadPlayerMeta = (opt) => {
  const r = opt.raw || {};
  // `opt.id` is the option's own key (a pitch slot id in the "Replaces Player"
  // picker), so prefer an explicit playerId when the option carries one.
  return [
    { label: "Player ID", value: opt.playerId ?? opt.id },
    { label: "Jersey Number", value: opt.num !== "" ? opt.num : jerseyNumberOf(r) },
    { label: "Position", value: opt.pos },
    { label: "Sub Position", value: opt.posSub },
    { label: "Email", value: r.email || r.player_email },
    { label: "Mobile", value: r.mobile || r.mobile_number },
  ];
};

const getInitials = (name) => {
  if (!name) return "?";
  const parts = name.split(" ");
  return (parts[0]?.[0] || "") + (parts[parts.length - 1]?.[0] || "");
};

// ─── Football Pitch SVG ───
const PitchSVG = ({ isDark }) => {
  const stripe1 = isDark ? "#1e2230" : "#e8ecf0";
  const stripe2 = isDark ? "#242838" : "#dfe4e9";
  const lineColor = isDark ? "rgba(255,255,255,0.25)" : "rgba(0,0,0,0.15)";
  const goalColor = isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.1)";

  return (
  <svg
    viewBox="0 0 1000 560"
    style={{
      position: "absolute",
      inset: 0,
      width: "100%",
      height: "100%",
    }}
    preserveAspectRatio="xMidYMid meet"
  >
    <defs>
      <pattern id="grass" width="100" height="560" patternUnits="userSpaceOnUse">
        <rect width="50" height="560" fill={stripe1} />
        <rect x="50" width="50" height="560" fill={stripe2} />
      </pattern>
    </defs>
    <rect width="1000" height="560" fill="url(#grass)" rx="6" />
    {/* Pitch outline */}
    <rect x="30" y="30" width="940" height="500" fill="none" stroke={lineColor} strokeWidth="2" />
    {/* Halfway line */}
    <line x1="500" y1="30" x2="500" y2="530" stroke={lineColor} strokeWidth="2" />
    {/* Center circle */}
    <circle cx="500" cy="280" r="65" fill="none" stroke={lineColor} strokeWidth="2" />
    <circle cx="500" cy="280" r="3" fill={lineColor} />
    {/* Left penalty area */}
    <rect x="30" y="158" width="136" height="244" fill="none" stroke={lineColor} strokeWidth="2" />
    <rect x="30" y="216" width="45" height="128" fill="none" stroke={lineColor} strokeWidth="2" />
    <circle cx="142" cy="280" r="3" fill={lineColor} />
    <path d="M 166 218 A 65 65 0 0 1 166 342" fill="none" stroke={lineColor} strokeWidth="2" />
    {/* Right penalty area */}
    <rect x="834" y="158" width="136" height="244" fill="none" stroke={lineColor} strokeWidth="2" />
    <rect x="925" y="216" width="45" height="128" fill="none" stroke={lineColor} strokeWidth="2" />
    <circle cx="858" cy="280" r="3" fill={lineColor} />
    <path d="M 834 218 A 65 65 0 0 0 834 342" fill="none" stroke={lineColor} strokeWidth="2" />
    {/* Goals */}
    <rect x="10" y="234" width="20" height="92" fill="none" stroke={goalColor} strokeWidth="2" rx="2" />
    <rect x="970" y="234" width="20" height="92" fill="none" stroke={goalColor} strokeWidth="2" rx="2" />
    {/* Corner arcs */}
    <path d="M 30 40 A 10 10 0 0 1 40 30" fill="none" stroke={lineColor} strokeWidth="2" />
    <path d="M 960 30 A 10 10 0 0 1 970 40" fill="none" stroke={lineColor} strokeWidth="2" />
    <path d="M 40 530 A 10 10 0 0 1 30 520" fill="none" stroke={lineColor} strokeWidth="2" />
    <path d="M 970 520 A 10 10 0 0 1 960 530" fill="none" stroke={lineColor} strokeWidth="2" />
  </svg>
  );
};

// ─── Player Token on Field ───
const PlayerToken = ({ slot, data, color, editMode, onClick, onDragStart, onDrop, dragOver, setDragOver, isDark, compact, vertical, substituted, subInfo }) => {
  const isEmpty = !data;
  const size = compact ? 30 : 44;
  const isOver = dragOver === slot.id;
  const team = slot.id.startsWith("h_") ? "home" : "away";

  const handleDragStart = (e) => {
    if (!editMode || isEmpty) return;
    e.dataTransfer.setData("lp/slot", JSON.stringify({ slotId: slot.id, team }));
    e.dataTransfer.effectAllowed = "move";
    if (onDragStart) onDragStart(slot.id);
  };

  const handleDragOver = (e) => {
    if (!editMode) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (setDragOver) setDragOver(slot.id);
  };

  const handleDragLeave = () => {
    if (setDragOver) setDragOver(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (setDragOver) setDragOver(null);
    if (onDrop) onDrop(slot, e);
  };

  return (
    <Box
      onClick={editMode ? onClick : undefined}
      draggable={editMode && !isEmpty}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      sx={{
        position: "absolute",
        // Vertical pitch (mobile): home → top, away → bottom. Swap x/y so tokens
        // stay upright while the pitch background is rotated 90°.
        left: `${vertical ? slot.y : slot.x}%`,
        top: `${vertical ? slot.x : slot.y}%`,
        transform: isOver
          ? "translate(-50%, -50%) scale(1.15)"
          : "translate(-50%, -50%)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "2px",
        cursor: editMode ? (isEmpty ? "pointer" : "grab") : "default",
        zIndex: isOver ? 20 : 10,
        transition: "transform 0.15s, box-shadow 0.15s",
        "&:hover": editMode
          ? { transform: "translate(-50%, -50%) scale(1.1)" }
          : {},
        "&:active": editMode && !isEmpty ? { cursor: "grabbing" } : {},
      }}
    >
      {isEmpty ? (
        editMode && (
          <>
            <Box
              sx={{
                width: size,
                height: size,
                borderRadius: "50%",
                border: isOver
                  ? `2.5px solid ${color}`
                  : `2px dashed ${color}80`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: isOver ? `${color}40` : `${color}15`,
                boxShadow: isOver ? `0 0 12px ${color}60` : "none",
                transition: "all 0.15s",
                "&:hover": {
                  backgroundColor: `${color}30`,
                  borderColor: color,
                },
              }}
            >
              <Add sx={{ fontSize: 20, color: `${color}90` }} />
            </Box>
            <Typography
              sx={{
                fontSize: "9px",
                fontWeight: 600,
                color: isDark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.4)",
                textTransform: "uppercase",
              }}
            >
              {slot.pos}
            </Typography>
          </>
        )
      ) : (
        <Tooltip
          arrow
          placement="top"
          title={
            <Box sx={{ py: 0.25 }}>
              <Typography sx={{ fontSize: "12px", fontWeight: 700 }}>{data.name}</Typography>
              <Typography sx={{ fontSize: "10px", opacity: 0.85 }}>
                #{data.num} · {slot.pos}
                {data.timeIn != null ? ` · in ${data.timeIn}'` : ""}
                {data.timeOut != null ? ` · out ${data.timeOut}'` : ""}
              </Typography>
              {subInfo && (
                <Box sx={{ mt: 0.5, pt: 0.5, borderTop: "1px solid rgba(255,255,255,0.25)" }}>
                  <Typography sx={{ fontSize: "10px", fontWeight: 700 }}>
                    ↓ Substituted off{subInfo.timeIn != null ? ` at ${subInfo.timeIn}'` : ""}
                  </Typography>
                  <Typography sx={{ fontSize: "10px", opacity: 0.85 }}>
                    Replaced by {subInfo.name}
                    {subInfo.num != null && subInfo.num !== "" ? ` · #${subInfo.num}` : ""}
                    {subInfo.pos ? ` · ${subInfo.pos}` : ""}
                  </Typography>
                </Box>
              )}
            </Box>
          }
        >
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "2px" }}>
          <Box sx={{ position: "relative" }}>
            <Box
              sx={{
                width: size,
                height: size,
                borderRadius: "50%",
                border: `2.5px solid ${color}`,
                backgroundColor: `${color}DD`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: isOver
                  ? `0 0 16px ${color}80`
                  : `0 2px 8px ${color}40`,
                transition: "box-shadow 0.15s",
              }}
            >
              <Typography
                sx={{
                  fontSize: "13px",
                  fontWeight: 700,
                  color: "#fff",
                  letterSpacing: "0.5px",
                }}
              >
                {getInitials(data.name)}
              </Typography>
            </Box>
            <Box
              sx={{
                position: "absolute",
                bottom: -2,
                right: -4,
                backgroundColor: isDark ? "#1a1a2e" : "#fff",
                border: `1.5px solid ${color}`,
                borderRadius: "4px",
                px: "4px",
                py: "1px",
                minWidth: "18px",
                textAlign: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: "9px",
                  fontWeight: 700,
                  color: isDark ? "#fff" : color,
                  lineHeight: 1,
                }}
              >
                #{data.num}
              </Typography>
            </Box>
            {substituted && (
              <Box
                sx={{
                  position: "absolute",
                  top: -4,
                  left: -4,
                  width: 18,
                  height: 18,
                  borderRadius: "50%",
                  backgroundColor: "#EF4444",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: `1.5px solid ${isDark ? "#1a1a2e" : "#fff"}`,
                  boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                }}
              >
                <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#fff", lineHeight: 1 }}>
                  ↓
                </Typography>
              </Box>
            )}
          </Box>
          {data.id != null && (
            <Typography
              sx={{
                fontSize: "8px",
                fontWeight: 600,
                color: isDark ? "rgba(255,255,255,0.6)" : "var(--text-2)",
                textAlign: "center",
              }}
            >
              ID: {data.id}
            </Typography>
          )}
          <Typography
            sx={{
              fontSize: "10px",
              fontWeight: 600,
              color: isDark ? "#fff" : "var(--text-1)",
              textAlign: "center",
              maxWidth: "70px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              textShadow: isDark ? "0 1px 3px rgba(0,0,0,0.6)" : "none",
            }}
          >
            {data.name.split(" ")[0]}
          </Typography>
          {data.timeIn != null && (
            <Typography
              sx={{
                fontSize: "8px",
                color: isDark ? "rgba(255,255,255,0.6)" : "var(--text-2)",
                textAlign: "center",
              }}
            >
              {data.timeIn}'{data.timeOut != null ? ` → ${data.timeOut}'` : ""}
            </Typography>
          )}
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};

// ─── Squad Panel (Sidebar) ───
const SquadPanel = ({ team, squad, lineupData, color, onPlayerClick, onCreatePlayer, subNames = [] }) => {
  const placedNames = Object.values(lineupData)
    .filter(Boolean)
    .map((d) => d.name);
  // Exclude both placed (starting XI) and substitute players from the list.
  const available = squad.filter(
    (p) => !placedNames.includes(p.name) && !subNames.includes(p.name)
  );
  const teamSlots = team === "home" ? ALL_HOME_SLOTS : ALL_AWAY_SLOTS;
  const filledCount = teamSlots.filter((s) => lineupData[s.id]).length;

  return (
    <Box
      sx={{
        width: { xs: "100%", lg: "200px" },
        minWidth: { lg: "180px" },
        flexShrink: 0,
        backgroundColor: "var(--surface-1)",
        border: "1px solid var(--border-1)",
        borderRadius: "12px",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        // Cap height so the list scrolls instead of pushing the pitch off-screen
        // when stacked above/below it on mobile & tablet.
        maxHeight: { xs: 260, lg: "600px" },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          px: 1.5,
          py: 1,
          borderBottom: "1px solid var(--border-1)",
          backgroundColor: "var(--surface-2)",
        }}
      >
        <Stack direction="row" alignItems="center" gap={1}>
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: "50%",
              backgroundColor: color,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography sx={{ fontSize: "10px", fontWeight: 700, color: "#fff" }}>
              {team === "home" ? "H" : "A"}
            </Typography>
          </Box>
          <Typography
            sx={{
              fontSize: "13px",
              fontWeight: 600,
              color: "var(--text-1)",
            }}
          >
            {team === "home" ? "Home" : "Away"}
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" gap={0.5}>
          <Chip
            label={`${available.length}`}
            size="small"
            sx={{
              height: 20,
              fontSize: "11px",
              fontWeight: 700,
              backgroundColor: `${color}20`,
              color: color,
            }}
          />
          {onCreatePlayer && (
            <IconButton
              size="small"
              onClick={onCreatePlayer}
              sx={{
                width: 22,
                height: 22,
                backgroundColor: `${color}15`,
                color: color,
                "&:hover": { backgroundColor: `${color}30` },
              }}
            >
              <PersonAdd sx={{ fontSize: 14 }} />
            </IconButton>
          )}
        </Stack>
      </Stack>
      <Box sx={{ overflow: "auto", flex: 1 }}>
        {available.length === 0 ? (
          <Typography
            sx={{
              fontSize: "12px",
              color: "var(--text-2)",
              textAlign: "center",
              py: 3,
            }}
          >
            All players placed
          </Typography>
        ) : (
          available.map((player) => (
            <Stack
              key={player.id}
              direction="row"
              alignItems="center"
              draggable
              onDragStart={(e) => {
                e.dataTransfer.setData(
                  "lp/player",
                  JSON.stringify({ id: player.id, name: player.name, num: player.num, team })
                );
                e.dataTransfer.effectAllowed = "move";
              }}
              onClick={() => onPlayerClick(player)}
              sx={{
                px: 1.5,
                py: 0.75,
                gap: 1,
                cursor: "grab",
                borderBottom: "1px solid var(--border-1)",
                "&:hover": { backgroundColor: "var(--surface-2)" },
                "&:active": { cursor: "grabbing" },
                userSelect: "none",
              }}
            >
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  border: `2px solid ${color}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Typography
                  sx={{ fontSize: "9px", fontWeight: 700, color: color }}
                >
                  {getInitials(player.name)}
                </Typography>
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {player.id != null && (
                  <Typography sx={{ fontSize: "9px", fontWeight: 600, color: "var(--text-2)", lineHeight: 1.2 }}>
                    ID: {player.id}
                  </Typography>
                )}
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: 500,
                    color: "var(--text-1)",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {player.name}
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontSize: "10px",
                  fontWeight: 600,
                  color: "var(--text-2)",
                }}
              >
                {player.pos}
              </Typography>
            </Stack>
          ))
        )}
      </Box>
    </Box>
  );
};

// ─── Lineup PDF / Text Download Modal ───
const LineupDownloadModal = ({ open, onClose, pdfDetails, teamAName, teamBName }) => {
  const handleDownload = (url, filename) => {
    if (!url) return;
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename || "lineup.pdf");
    link.setAttribute("target", "_blank");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadText = (text, filename) => {
    if (!text) return;
    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename || "lineup.txt");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleCopyText = (text) => {
    if (text) {
      navigator.clipboard.writeText(text);
      toast.success("Lineup text copied to clipboard!");
    }
  };

  const hasCombined = !!pdfDetails?.pdf_url;
  const hasSeparate = !!(pdfDetails?.pdf_url_self || pdfDetails?.pdf_url_opponent);
  const hasText = !!pdfDetails?.lineup_details_text;

  const cardSx = {
    p: 2,
    borderRadius: "10px",
    border: "1px solid var(--border-1)",
    backgroundColor: "var(--surface-2)",
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          backgroundColor: "var(--surface-1)",
          borderRadius: "12px",
          border: "1px solid var(--border-1)",
          maxWidth: "450px",
          width: "100%",
        },
      }}
    >
      <DialogTitle sx={{ borderBottom: "1px solid var(--border-1)", p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography sx={{ fontSize: "16px", color: "var(--text-1)", fontWeight: 600 }}>
            Download Match Lineup
          </Typography>
          <IconButton onClick={onClose} sx={{ color: "var(--text-2)", p: 0.5 }}>
            <Close sx={{ fontSize: 20 }} />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ p: 2.5 }}>
        {!hasCombined && !hasSeparate && !hasText ? (
          <Typography sx={{ fontSize: "14px", color: "var(--text-2)", mt: 2 }}>
            No lineup available to download yet.
          </Typography>
        ) : (
          <>
            <Typography sx={{ fontSize: "14px", color: "var(--text-2)", mb: 2, mt: 2 }}>
              Select the format you would like to download or copy.
            </Typography>
            <Stack spacing={2}>
              {hasCombined && (
                <Box sx={{ ...cardSx, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <FaFilePdf size={24} color="#EF4444" />
                    <Box>
                      <Typography sx={{ color: "var(--text-1)", fontSize: "14px", fontWeight: 600 }}>
                        Combined Lineup
                      </Typography>
                      <Typography sx={{ color: "var(--text-2)", fontSize: "12px" }}>
                        Both teams in a single PDF
                      </Typography>
                    </Box>
                  </Stack>
                  <IconButton
                    onClick={() => handleDownload(pdfDetails.pdf_url, "combined_lineup.pdf")}
                    sx={{ color: "#3B82F6" }}
                  >
                    <FaDownload size={18} />
                  </IconButton>
                </Box>
              )}

              {hasSeparate && (
                <Box sx={cardSx}>
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                    <FaFilePdf size={24} color="#10B981" />
                    <Box>
                      <Typography sx={{ color: "var(--text-1)", fontSize: "14px", fontWeight: 600 }}>
                        Separate Team Lineups
                      </Typography>
                      <Typography sx={{ color: "var(--text-2)", fontSize: "12px" }}>
                        Individual PDF files for each team
                      </Typography>
                    </Box>
                  </Stack>
                  <Stack direction="row" spacing={1}>
                    {pdfDetails.pdf_url_self && (
                      <Button
                        fullWidth
                        size="small"
                        variant="outlined"
                        onClick={() => handleDownload(pdfDetails.pdf_url_self, `${teamAName || "team_a"}_lineup.pdf`)}
                        sx={{ color: "var(--text-1)", borderColor: "var(--border-1)", textTransform: "none", fontSize: "12px" }}
                        startIcon={<FaDownload size={12} />}
                      >
                        {teamAName || "Team A"}
                      </Button>
                    )}
                    {pdfDetails.pdf_url_opponent && (
                      <Button
                        fullWidth
                        size="small"
                        variant="outlined"
                        onClick={() => handleDownload(pdfDetails.pdf_url_opponent, `${teamBName || "team_b"}_lineup.pdf`)}
                        sx={{ color: "var(--text-1)", borderColor: "var(--border-1)", textTransform: "none", fontSize: "12px" }}
                        startIcon={<FaDownload size={12} />}
                      >
                        {teamBName || "Team B"}
                      </Button>
                    )}
                  </Stack>
                </Box>
              )}

              {hasText && (
                <Box sx={{ ...cardSx, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <FaFileAlt size={24} color="#F59E0B" />
                    <Box>
                      <Typography sx={{ color: "var(--text-1)", fontSize: "14px", fontWeight: 600 }}>
                        Raw Text Lineup
                      </Typography>
                      <Typography sx={{ color: "var(--text-2)", fontSize: "12px" }}>
                        Copy line-up details as text
                      </Typography>
                    </Box>
                  </Stack>
                  <Stack direction="row" spacing={1}>
                    <Tooltip title="Download as .txt">
                      <IconButton
                        onClick={() => handleDownloadText(pdfDetails.lineup_details_text, "match_lineup.txt")}
                        sx={{ color: "#F59E0B" }}
                      >
                        <FaDownload size={18} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Copy to clipboard">
                      <IconButton
                        onClick={() => handleCopyText(pdfDetails.lineup_details_text)}
                        sx={{ color: "#F59E0B" }}
                      >
                        <FaCopy size={18} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Box>
              )}
            </Stack>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ─── Main Component ───
const MatchLineUp = ({ matchId, matchInfo, token, readOnly = false }) => {
  const [editMode, setEditMode] = useState(false);
  const [lineupModalOpen, setLineupModalOpen] = useState(false);
  const [lineupData, setLineupData] = useState({});
  const [dragOver, setDragOver] = useState(null);
  // Reactive to theme toggles so the pitch re-colours in dark mode.
  const { mode } = useThemeMode();
  const isDark = mode === "dark";
  const isMobile = useMediaQuery("(max-width:600px)");
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const teamIdOf = (team) =>
    team === "home" ? matchInfo?.teamA?.id : matchInfo?.teamB?.id;
  const startersCap = matchInfo?.startingLineupCount || 11;
  // Full match duration (min) from match details — default time-out for subs.
  const matchGameTime = Number(matchInfo?.gameTime) > 0 ? Number(matchInfo.gameTime) : 90;

  // game_time (end − start) is always sent to the API, so it can never be 0 or
  // negative. An empty Time Out means the player plays to the final whistle.
  const gameTimeError = (timeIn, timeOut) => {
    const start = timeIn === "" || timeIn == null ? 0 : parseInt(timeIn);
    const hasEnd = timeOut !== "" && timeOut != null;
    const end = hasEnd ? parseInt(timeOut) : matchGameTime;
    if (!Number.isFinite(start) || start < 0) return "Time In can't be negative";
    if (hasEnd && (!Number.isFinite(end) || end < 0)) return "Time Out can't be negative";
    if (end - start <= 0)
      return hasEnd
        ? "Time Out must be after Time In (game time must be at least 1 min)"
        : `Time In must be less than the match duration (${matchGameTime} min)`;
    return null;
  };

  // Player modal
  const [playerModal, setPlayerModal] = useState(null);
  const [playerForm, setPlayerForm] = useState({
    id: null,
    name: "",
    num: "",
    timeIn: "0",
    timeOut: "",
  });

  // Sub modal
  const [subModal, setSubModal] = useState(null);
  const [subForm, setSubForm] = useState({
    id: null,
    name: "",
    num: "",
    timeIn: "",
    timeOut: "",
    replacesSlot: "",
    pos: "",
  });

  // TODO: API — fetch existing lineup data for this match
  // TODO: API — fetch squad rosters for both teams

  const [homeSquad, setHomeSquad] = useState([]);
  const [awaySquad, setAwaySquad] = useState([]);

  // Per-player snapshot of the lineup exactly as it was loaded, keyed by
  // user_id. The save diff compares against THIS so only players the analyst
  // actually touched (or added / removed) end up in the payload.
  const loadedRef = useRef(new Map());

  // Create player modal (reached from the search dialog's "Create New Player").
  // On submit it ALSO runs a global search and then follows the duplicate
  // decision table (duplicate-flow spec §15/§20).
  const [createPlayerModal, setCreatePlayerModal] = useState(null);
  const [createBusy, setCreateBusy] = useState(false);
  // Normalised duplicate envelope from search / create — drives the actions.
  const [createDup, setCreateDup] = useState(null);
  // Duplicate-block message shown inline on the create modal.
  const [createError, setCreateError] = useState(null);
  const createMatches = useMemo(() => groupDuplicateRows(createDup?.data), [createDup]);
  const [createForm, setCreateForm] = useState(EMPTY_PLAYER_FORM);

  // Slot that kicked off a global-search / create flow, so the new player can
  // be handed straight back to that lineup position once they exist.
  const [pendingSlot, setPendingSlot] = useState(null); // { slotId, team }

  const openCreatePlayer = (team, prefillName, slotId = null) => {
    setPendingSlot(slotId ? { slotId, team } : null);
    const [firstName = "", ...rest] = String(prefillName || "").trim().split(" ");
    setCreateForm({ ...EMPTY_PLAYER_FORM, firstName, lastName: rest.join(" ") });
    setCreateDup(null);
    setCreateError(null);
    setCreatePlayerModal({ team });
  };
  const closeCreatePlayer = () => {
    setCreatePlayerModal(null);
    setCreateDup(null);
    setCreateError(null);
  };

  // Payload shared by create / force-create / approval-request (spec §9/§10).
  const createUserData = (teamId) => ({
    first_name: createForm.firstName,
    last_name: createForm.lastName,
    email: createForm.email || "",
    mobile: createForm.phone || "",
    country_code: createForm.countryCode || "",
    preferred_position: createForm.position || 0,
    preferred_foot: createForm.preferredFoot || 0,
    // Carried so the reviewer sees — and approval creates — the full profile.
    dob: createForm.dob || "",
    height: createForm.height || 0,
    weight: createForm.weight || 0,
    jersey_number: createForm.jerseyNumber || "",
    category: createForm.isDummy ? 0 : 1,
    team_id: teamId || 0,
    ...(matchInfo?.organizerId ? { organizer_id: matchInfo.organizerId } : {}),
  });

  // Create the player (createUser tags them to the team), then reload.
  // Creation re-validates duplicates server-side (spec §18) — a blocked
  // response drops the user back into the duplicate-handling view.
  const doCreateUser = async () => {
    const teamId = teamIdOf(createPlayerModal?.team);
    setCreateBusy(true);
    setCreateError(null);
    try {
      const created = await createUser({ form: createForm, teamId, organizerId: matchInfo?.organizerId, token });
      toast.success("Player created");
      closeCreatePlayer();
      const squads = await loadSquads();
      if (resumePendingSlot(created?.user_id, squads)) {
        toast.success("Pick the position details and save to place them");
      }
    } catch (err) {
      if (err?.blocked) {
        // Spec §8.1 — back to the duplicate flow with whatever rows came back.
        setCreateDup(err.duplicate);
        setCreateError(err.duplicate?.data?.length ? null : err.message);
        return;
      }
      console.error("[MatchLineUp] create player failed:", err?.message);
      toast.error("Failed to create player");
    } finally {
      setCreateBusy(false);
    }
  };

  // Submit: dummy → create directly; else global-search first and follow the
  // duplicate decision table.
  const handleCreatePlayer = async () => {
    if (!createForm.firstName) return;
    if (createForm.isDummy) return doCreateUser();
    setCreateBusy(true);
    setCreateError(null);
    try {
      const name = `${createForm.firstName} ${createForm.lastName}`.trim();
      const dup = await searchPlayerGlobal({ name, organizerId: matchInfo?.organizerId, token });
      // NO_DUPLICATE / DIFFERENT_COUNTRY → straight to create (spec §3.1/§3.2).
      if (isCreatableDuplicate(dup.duplicateType) || (!dup.blocked && !dup.data.length)) {
        setCreateBusy(false);
        return doCreateUser();
      }
      setCreateDup(dup);
      // Too many matches (spec §5) → message only, no rows to act on.
      if (!dup.data.length) setCreateError(dup.message || "Duplicate players found");
    } catch (err) {
      console.error("[MatchLineUp] player search failed:", err?.message);
      setCreateError("Player search failed. Please try again.");
    } finally {
      setCreateBusy(false);
    }
  };

  // Attach an existing matched player to the requested team. A player from
  // another organizer ("Port to Organizer") is ported first, then re-teamed.
  const portMatchedPlayer = async (player) => {
    const { teamId, orgId, action } = playerAddState(player, createPlayerModal?.team);
    if (!teamId || player?.id == null) return;
    setCreateBusy(true);
    try {
      if (action === "PORT_ORG" && orgId) {
        await portPlayerToOrganizer({ playerIds: [player.id], organizerId: orgId, token });
      }
      const ok = await addPlayersToTeam({ teamId, userIds: [player.id], organizerId: orgId, token });
      if (!ok) throw new Error("updateTeam returned false");
      toast.success("Player added to team");
      closeCreatePlayer();
      resumePendingSlot(player.id, await loadSquads());
    } catch (err) {
      console.error("[MatchLineUp] add matched player failed:", err?.message);
      toast.error("Failed to add player");
    } finally {
      setCreateBusy(false);
    }
  };

  // Matched player is already in the requested team → nothing to call (spec §4.1).
  const selectMatchedPlayer = async (player) => {
    toast.success("Player already in this team");
    closeCreatePlayer();
    return resumePendingSlot(player?.id, await loadSquads());
  };

  // SAME_COUNTRY → override directly via raise_duplicate_player_flag (spec §9):
  // no admin approval, the player is created immediately with the same payload.
  const overrideCreateViaFlag = async () => {
    const teamId = teamIdOf(createPlayerModal?.team);
    setCreateBusy(true);
    setCreateError(null);
    try {
      const res = await raiseDuplicatePlayerFlag({
        token,
        body: {
          user_data: createUserData(teamId),
          potential_duplicates_data: createDup?.data ?? [],
        },
      });
      toast.success(res?.user_id ? "Player created" : "Flag raised");
      closeCreatePlayer();
      const squads = await loadSquads();
      if (resumePendingSlot(res?.user_id, squads)) {
        toast.success("Pick the position details and save to place them");
      }
    } catch (err) {
      if (err?.blocked) {
        setCreateDup(err.duplicate);
        setCreateError(err.duplicate?.data?.length ? null : err.message);
        return;
      }
      console.error("[MatchLineUp] raise flag failed:", err?.message);
      toast.error(err?.response?.data?.response_message || "Failed to create player");
    } finally {
      setCreateBusy(false);
    }
  };

  // "Raise Request to Admin" — the player is NOT created yet (spec §10).
  const raiseApprovalRequest = async () => {
    const teamId = teamIdOf(createPlayerModal?.team);
    setCreateBusy(true);
    try {
      const res = await requestDuplicatePlayerApproval({
        token,
        userData: createUserData(teamId),
        duplicates: createDup?.data ?? [],
      });
      toast.success(
        res?.request_id
          ? "Request submitted for Admin approval."
          : "Request submitted."
      );
      closeCreatePlayer();
    } catch (err) {
      const msg = err?.response?.data?.response_message;
      console.error("[MatchLineUp] approval request failed:", err?.message);
      toast.error(msg || "Failed to raise approval request");
    } finally {
      setCreateBusy(false);
    }
  };

  // ─── Search & add existing players to a team (spec §3/§4/§6/§14) ───
  const [addModal, setAddModal] = useState(null); // { team }
  const [playerSearch, setPlayerSearch] = useState("");
  const [searchDup, setSearchDup] = useState(null);
  const [searching, setSearching] = useState(false);
  const [addingId, setAddingId] = useState(null);
  const searchMatches = useMemo(() => groupDuplicateRows(searchDup?.data), [searchDup]);

  const openAddPlayer = (team, slotId = null) => {
    setPendingSlot(slotId ? { slotId, team } : null);
    setAddModal({ team });
    setPlayerSearch("");
    setSearchDup(null);
  };

  const searchOrganizerId = matchInfo?.organizerId;
  const runPlayerSearch = useCallback(
    async (rawName) => {
      const q = String(rawName ?? "").trim();
      if (!q) {
        setSearchDup(null);
        return;
      }
      setSearching(true);
      try {
        const dup = await searchPlayerGlobal({ name: q, organizerId: searchOrganizerId, token });
        setSearchDup(dup);
      } catch (err) {
        console.error("[MatchLineUp] player search failed:", err?.message);
        toast.error("Player search failed");
      } finally {
        setSearching(false);
      }
    },
    [searchOrganizerId, token]
  );

  // Spec §3 — search as the analyst types (debounced).
  useEffect(() => {
    if (!addModal) return;
    const q = playerSearch.trim();
    if (q.length < 3) {
      setSearchDup(null);
      return;
    }
    const t = setTimeout(() => runPlayerSearch(q), 400);
    return () => clearTimeout(t);
  }, [playerSearch, addModal, runPlayerSearch]);

  // Action for one grouped match, decided from that player's OWN memberships
  // rather than the response-level duplicate_type — a single search can mix
  // same-organizer and same-country hits (spec §4 / §6).
  //  SELECT    – already in the requested team, no API call
  //  PORT_TEAM – already in this organizer, another team → updateTeam
  //  PORT_ORG  – belongs to another organizer → port_entity + updateTeam
  const playerAddState = (player, teamRef) => {
    const teamId = teamIdOf(teamRef);
    const orgId = matchInfo?.organizerId;
    // The loaded roster is the authority on "already here": the search may
    // only return this player's OTHER memberships, so a same-id player sitting
    // in the requested squad must still read as Select, not as a port.
    const squad = teamRef === "home" ? homeSquad : awaySquad;
    const memberships = player.memberships || [];
    const inTeam =
      squad.some((s) => sameId(s.id, player.id)) ||
      memberships.some((m) => sameId(m.teamId, teamId));
    const inOrganizer =
      orgId == null || memberships.some((m) => sameId(m.organizerId, orgId));
    const action = inTeam ? "SELECT" : inOrganizer ? "PORT_TEAM" : "PORT_ORG";

    // Show only the membership the action is actually about: the other clubs a
    // player turns out for are irrelevant once we know they're already here.
    //   SELECT    → this team
    //   PORT_TEAM → their team(s) inside this organizer
    //   PORT_ORG  → the organizer(s) they'd be ported from
    const scoped = inTeam
      ? memberships.filter((m) => sameId(m.teamId, teamId))
      : inOrganizer
      ? memberships.filter((m) => sameId(m.organizerId, orgId))
      : memberships;
    const otherOrganizers = uniqueField(
      memberships.filter((m) => !sameId(m.organizerId, orgId)),
      "organizerName"
    );

    const currentTeamName =
      teamRef === "home" ? matchInfo?.teamA?.name : matchInfo?.teamB?.name;
    // Nothing scoped means Select came from the roster alone (the search only
    // returned this player's OTHER clubs) — fall back to the match's own team.
    const teamNames = scoped.length
      ? uniqueField(scoped, "teamName")
      : [inTeam ? currentTeamName : null].filter(Boolean);
    // Select and Port to Team both leave the player under THIS organizer, so
    // the row states this organizer. The API row's organizer_name tracks the
    // player's own/most-played organizer, not the owner of team_id, so echoing
    // it here reads as a cross-organizer match when it isn't one.
    const organizerNames =
      action === "PORT_ORG"
        ? uniqueField(scoped, "organizerName")
        : [matchInfo?.organizerName].filter(Boolean);

    return {
      teamId,
      orgId,
      action,
      teamNames,
      organizerNames,
      label:
        action === "SELECT"
          ? "Select"
          : action === "PORT_ORG"
          ? // Porting alone doesn't put them in the squad — the team add always
            // follows, so the button says both.
            "Port to Organizer & Add to Team"
          : "Port to Team",
      hint:
        action === "SELECT"
          ? "Same player — already in this team"
          : action === "PORT_ORG"
          ? `Belongs to ${otherOrganizers.join(", ") || "another organizer"} — ports the player into this organizer, then adds to this team`
          : "Already in this organizer — just adds the player to this team",
    };
  };

  const addSearchedPlayer = async (player) => {
    const { teamId, orgId, action } = playerAddState(player, addModal?.team);
    if (!teamId || player?.id == null) return;
    if (action === "SELECT") {
      setAddModal(null);
      toast.success("Player already in this team");
      return resumePendingSlot(player.id, await loadSquads());
    }
    setAddingId(player.id);
    try {
      // Another organizer → port first, then (re)assign the team.
      if (action === "PORT_ORG" && orgId) {
        await portPlayerToOrganizer({ playerIds: [player.id], organizerId: orgId, token });
      }
      const ok = await addPlayersToTeam({ teamId, userIds: [player.id], organizerId: orgId, token });
      if (!ok) throw new Error("updateTeam returned false");
      toast.success("Player added to team");
      setAddModal(null);
      resumePendingSlot(player.id, await loadSquads());
    } catch (err) {
      console.error("[MatchLineUp] add player failed:", err?.message);
      toast.error("Failed to add player");
    } finally {
      setAddingId(null);
    }
  };

  // "Create New Player" from the search results → open the create form,
  // carrying the originating slot through.
  const openCreateFromSearch = () => {
    const team = addModal?.team;
    const slotId = pendingSlot?.slotId ?? null;
    setAddModal(null);
    openCreatePlayer(team, playerSearch, slotId);
  };

  /**
   * Hand a just-created / just-ported player back to the lineup slot the flow
   * started from: reopen that slot's modal with the player preselected and the
   * jersey prefilled, so the existing Save path (and its validation) commits
   * the mapping. Returns true when a slot was pending.
   */
  const resumePendingSlot = (playerId, squads) => {
    if (!pendingSlot || playerId == null) return false;
    const { slotId, team } = pendingSlot;
    const roster = (team === "home" ? squads?.home : squads?.away) || [];
    const player = roster.find((x) => String(x.id) === String(playerId));
    if (!player) return false;
    setPendingSlot(null);
    setPlayerForm({
      id: player.id ?? playerId,
      name: player.name,
      num: player.num != null ? String(player.num) : "",
      timeIn: "0",
      timeOut: "",
    });
    setPlayerModal({
      slotId,
      pos: ALL_SLOTS.find((s) => s.id === slotId)?.pos ?? null,
      team,
    });
    return true;
  };

  const openSlotModal = useCallback(
    (slot) => {
      const existing = lineupData[slot.id];
      setPlayerForm(
        existing
          ? {
              id: existing.id ?? null,
              name: existing.name,
              num: String(existing.num),
              timeIn: String(existing.timeIn ?? 0),
              timeOut: existing.timeOut != null ? String(existing.timeOut) : "",
            }
          : { id: null, name: "", num: "", timeIn: "0", timeOut: "" }
      );
      setPlayerModal({
        slotId: slot.id,
        pos: slot.pos,
        team: slot.id.startsWith("h_") ? "home" : "away",
      });
    },
    [lineupData]
  );

  const handleSlotDrop = useCallback(
    (targetSlot, e) => {
      const targetTeam = targetSlot.id.startsWith("h_") ? "home" : "away";

      // Case 1: squad player dragged onto slot — open modal pre-filled
      const playerJson = e.dataTransfer.getData("lp/player");
      if (playerJson) {
        try {
          const player = JSON.parse(playerJson);
          if (player.team !== targetTeam) return;
          setPlayerForm({
            id: player.id ?? null,
            name: player.name,
            num: String(player.num),
            timeIn: "0",
            timeOut: "",
          });
          setPlayerModal({
            slotId: targetSlot.id,
            pos: targetSlot.pos,
            team: targetTeam,
          });
        } catch (_) {}
        return;
      }

      // Case 2: slot-to-slot swap
      const slotJson = e.dataTransfer.getData("lp/slot");
      if (slotJson) {
        try {
          const { slotId: srcId, team: srcTeam } = JSON.parse(slotJson);
          if (srcTeam !== targetTeam) return;
          if (srcId === targetSlot.id) return;
          setLineupData((prev) => {
            const next = { ...prev };
            const srcData = next[srcId] || null;
            const tgtData = next[targetSlot.id] || null;
            if (srcData) next[targetSlot.id] = srcData;
            else delete next[targetSlot.id];
            if (tgtData) next[srcId] = tgtData;
            else delete next[srcId];
            return next;
          });
          setSubs((prev) =>
            prev.map((sub) => {
              if (sub.replacesSlot === srcId) return { ...sub, replacesSlot: targetSlot.id };
              if (sub.replacesSlot === targetSlot.id) return { ...sub, replacesSlot: srcId };
              return sub;
            })
          );
        } catch (_) {}
      }
    },
    [lineupData]
  );

  const savePlayer = () => {
    if (!playerForm.name || !playerForm.num) return;
    const targetSlotId = playerModal.slotId || playerModal.selectedSlotId;
    if (!targetSlotId) return;
    // Cap the starting XI at the allowed lineup count when adding a NEW player
    // (replacing/editing an already-filled slot is fine).
    const isNewPlacement = !lineupData[targetSlotId];
    if (isNewPlacement && getPlacedSlots(playerModal.team).length >= startersCap) {
      toast.error(`Only ${startersCap} players allowed in the lineup`);
      return;
    }
    // Validate: the resulting game_time must be a positive number of minutes.
    const tIn = parseInt(playerForm.timeIn) || 0;
    const tOut = playerForm.timeOut !== "" && playerForm.timeOut != null ? parseInt(playerForm.timeOut) : null;
    const gtErr = gameTimeError(playerForm.timeIn, playerForm.timeOut);
    if (gtErr) {
      toast.error(gtErr);
      return;
    }
    const slotPos = ALL_SLOTS.find((s) => s.id === targetSlotId)?.pos ?? null;
    setLineupData((prev) => ({
      ...prev,
      [targetSlotId]: {
        id: playerForm.id ?? prev[targetSlotId]?.id ?? null,
        name: playerForm.name,
        num: parseInt(playerForm.num),
        pos: slotPos,
        timeIn: tIn,
        timeOut: tOut,
      },
    }));
    setPlayerModal(null);
  };

  const removePlayer = () => {
    setLineupData((prev) => {
      const next = { ...prev };
      delete next[playerModal.slotId];
      return next;
    });
    setPlayerModal(null);
  };

  const handleSquadPlayerClick = (player, team) => {
    if (!editMode) return;
    setPlayerForm({
      id: player.id ?? null,
      name: player.name,
      num: String(player.num),
      timeIn: "0",
      timeOut: "",
    });
    setPlayerModal({
      slotId: null,
      pos: null,
      team,
      pickPosition: true,
    });
  };

  const openSubModal = (team, editIdx = null, presetReplaceId = null) => {
    if (editIdx != null) {
      const sub = subs[editIdx];
      setSubForm({
        id: sub.playerId ?? null,
        name: sub.name,
        num: String(sub.num),
        timeIn: String(sub.timeIn),
        timeOut: String(sub.timeOut ?? matchGameTime),
        replacesSlot: sub.replacesSlot || "",
        pos: sub.pos || resolveReplaced(sub.replacesSlot)?.pos || "",
      });
    } else {
      setSubForm({
        id: null,
        name: "",
        num: "",
        timeIn: "",
        timeOut: String(matchGameTime),
        replacesSlot: presetReplaceId || "",
        pos: presetReplaceId ? resolveReplaced(presetReplaceId)?.pos || "" : "",
      });
    }
    setSubModal({ team, editIdx });
  };

  const saveSub = () => {
    if (!subForm.name || !subForm.num || !subForm.timeIn) return;
    if (!subForm.replacesSlot) {
      toast.error("Select which player was substituted out");
      return;
    }
    // The sub's time-in becomes the replaced player's end time — it can't be
    // before that player's start time.
    const subTime = parseInt(subForm.timeIn) || 0;
    const replacedStart = resolveReplaced(subForm.replacesSlot)?.timeIn;
    if (replacedStart != null && subTime < replacedStart) {
      toast.error("Substitution time can't be before the replaced player's start time");
      return;
    }
    const subOut = subForm.timeOut === "" || subForm.timeOut == null ? null : parseInt(subForm.timeOut);
    const gtErr = gameTimeError(subForm.timeIn, subForm.timeOut);
    if (gtErr) {
      toast.error(gtErr);
      return;
    }
    const entry = {
      id: `sub_${Date.now()}`,
      playerId: subForm.id ?? null,
      team: subModal.team,
      name: subForm.name,
      num: parseInt(subForm.num),
      timeIn: parseInt(subForm.timeIn),
      timeOut: subOut,
      replacesSlot: subForm.replacesSlot || null,
      pos: subForm.pos || null,
    };

    if (subForm.replacesSlot && lineupData[subForm.replacesSlot]) {
      setLineupData((prev) => ({
        ...prev,
        [subForm.replacesSlot]: {
          ...prev[subForm.replacesSlot],
          timeOut: parseInt(subForm.timeIn),
        },
      }));
    }

    if (subModal.editIdx != null) {
      setSubs((prev) =>
        prev.map((s, i) =>
          i === subModal.editIdx ? { ...entry, id: s.id } : s
        )
      );
    } else {
      setSubs((prev) => [...prev, entry]);
    }
    setSubModal(null);
  };

  const deleteSub = (idx) => {
    const sub = subs[idx];
    if (sub.replacesSlot && lineupData[sub.replacesSlot]) {
      setLineupData((prev) => ({
        ...prev,
        [sub.replacesSlot]: {
          ...prev[sub.replacesSlot],
          timeOut: null,
        },
      }));
    }
    setSubs((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSave = async () => {
    if (!matchId) return;
    const tournamentId = matchInfo?.tournamentId ?? null;
    const teamName = (team) => (team === "home" ? matchInfo?.teamA?.name : matchInfo?.teamB?.name);

    // Group placed starters by team
    const startersByTeam = { home: [], away: [] };
    Object.keys(lineupData).forEach((slotId) => {
      const team = slotId.startsWith("h_") ? "home" : "away";
      startersByTeam[team].push({
        slot: ALL_SLOTS.find((s) => s.id === slotId),
        d: lineupData[slotId],
      });
    });

    // Validation: starting XI cap
    for (const team of ["home", "away"]) {
      if (startersByTeam[team].length > startersCap) {
        toast.error(`${teamName(team)}: max ${startersCap} starters allowed`);
        return;
      }
    }
    // Validation: jersey uniqueness per team (starters + subs)
    for (const team of ["home", "away"]) {
      const nums = [
        ...startersByTeam[team].map((x) => String(x.d.num)),
        ...subs.filter((s) => s.team === team).map((s) => String(s.num)),
      ].filter((n) => n !== "" && n !== "null" && n != null);
      const dup = nums.find((n, i) => nums.indexOf(n) !== i);
      if (dup) {
        toast.error(`${teamName(team)}: jersey #${dup} is used by two players`);
        return;
      }
    }

    const mappings = [];
    const inLineupIds = new Set();
    // Current per-player state, in the same shape as the load snapshot.
    const curState = new Map();

    // Starting XI → lineup_notation 1
    ["home", "away"].forEach((team) => {
      const teamSlots = team === "home" ? ALL_HOME_SLOTS : ALL_AWAY_SLOTS;
      startersByTeam[team].forEach(({ slot, d }) => {
        if (d.id != null) inLineupIds.add(d.id);
        const start = d.timeIn ?? 0;
        const end = d.timeOut ?? null;
        // sub-level (0=middle, 1=top, 2=bottom) so the up/middle/down
        // placement round-trips on the next load.
        const group = slot
          ? teamSlots.filter((s) => s.pos === slot.pos).sort((a, b) => a.y - b.y)
          : [];
        const gIdx = slot ? group.findIndex((s) => s.id === slot.id) : -1;
        const subLvl = gIdx >= 0 ? subLevelForSlotIndex(gIdx, group.length) : -1;
        curState.set(String(d.id), {
          n: 1,
          slot: slot?.id ?? null,
          pos: null,
          num: d.num,
          tIn: start,
          tOut: end,
          rep: null,
        });
        mappings.push({
          user_id: d.id,
          match_id: Number(matchId),
          team_id: teamIdOf(team),
          tournament_id: tournamentId,
          player_name: d.name,
          jersey_number: d.num,
          preferred_position: posId(slot?.pos ?? d.pos),
          preferred_position_sub_level: subLvl >= 0 ? subLvl : null,
          lineup_notation: 1,
          start_gametime: start,
          end_gametime: end,
          game_time: (end != null ? end : matchGameTime) - (start || 0),
          substituted_player_id: null,
          substituted_player_name: null,
        });
      });
    });

    // Substitutes → lineup_notation 2
    subs.forEach((sub) => {
      if (sub.playerId != null) inLineupIds.add(sub.playerId);
      const replaced = resolveReplaced(sub.replacesSlot);
      const start = sub.timeIn ?? 0;
      const subEnd = sub.timeOut ?? null;
      curState.set(String(sub.playerId), {
        n: 2,
        slot: null,
        pos: sub.pos ?? null,
        num: sub.num,
        tIn: start,
        tOut: subEnd,
        rep: replaced?.id ?? null,
      });
      mappings.push({
        user_id: sub.playerId,
        match_id: Number(matchId),
        team_id: teamIdOf(sub.team),
        tournament_id: tournamentId,
        player_name: sub.name,
        jersey_number: sub.num,
        preferred_position: posId(sub.pos),
        preferred_position_sub_level: null,
        lineup_notation: 2,
        start_gametime: start,
        end_gametime: subEnd,
        game_time: (subEnd != null ? subEnd : matchGameTime) - (start || 0),
        substituted_player_id: replaced?.id ?? null,
        substituted_player_name: replaced?.name ?? null,
      });
    });

    // Removals → lineup_notation null (was in lineup at fetch, now gone)
    [
      ...homeSquad.map((p) => ({ p, team: "home" })),
      ...awaySquad.map((p) => ({ p, team: "away" })),
    ].forEach(({ p, team }) => {
      const was = p.umd?.lineup_notation === 1 || p.umd?.lineup_notation === 2;
      if (was && !inLineupIds.has(p.id)) {
        curState.set(String(p.id), { n: null });
        mappings.push({
          user_id: p.id,
          match_id: Number(matchId),
          team_id: teamIdOf(team),
          tournament_id: tournamentId,
          player_name: p.name,
          jersey_number: p.num,
          preferred_position: posId(p.pos),
          preferred_position_sub_level: null,
          lineup_notation: null,
          start_gametime: 0,
          end_gametime: 0,
          game_time: 0,
          substituted_player_id: null,
          substituted_player_name: null,
        });
      }
    });

    // Only send rows the analyst actually changed (or added / removed) — a
    // substitution posts the substitute's row alone, not the whole lineup.
    const nrm = (v) => {
      if (v === "" || v == null) return null;
      const n = Number(v);
      return Number.isFinite(n) ? n : v;
    };
    // The backend closes out the replaced player from the substitute's
    // `substituted_player_id`, so their end time alone is not a change worth
    // sending.
    // Players closed out by a substitution — now, or in the loaded state (a
    // deleted sub reopens its replaced player, which the backend also derives
    // from that sub's row, so it needs no row of its own either).
    const substitutedOut = new Set(
      mappings
        .filter((r) => r.lineup_notation === 2 && r.substituted_player_id != null)
        .map((r) => String(r.substituted_player_id))
    );
    loadedRef.current.forEach((prev) => {
      if (prev?.n === 2 && prev.rep != null) substitutedOut.add(String(prev.rep));
    });
    const rowChanged = (uid) => {
      const prev = loadedRef.current.get(uid);
      const cur = curState.get(uid);
      if (!prev || !cur) return true; // newly added (or no snapshot) → send
      if (prev.n !== cur.n) return true; // starter ⇄ sub ⇄ removed
      if (cur.n == null) return false; // was already out of the lineup
      if (prev.slot !== cur.slot) return true; // moved to another pitch slot
      if (nrm(prev.pos) !== nrm(cur.pos)) return true;
      if (nrm(prev.num) !== nrm(cur.num)) return true;
      if (nrm(prev.tIn) !== nrm(cur.tIn)) return true;
      if (nrm(prev.rep) !== nrm(cur.rep)) return true;
      if (!substitutedOut.has(uid) && nrm(prev.tOut) !== nrm(cur.tOut)) return true;
      return false;
    };
    const changed = mappings.filter((row) => rowChanged(String(row.user_id)));

    if (changed.length === 0) {
      toast.success("No lineup changes to save");
      setEditMode(false);
      return;
    }

    setSaving(true);
    try {
      await saveLineupBulk({ mappings: changed, organizerId: matchInfo?.organizerId, token });
      toast.success("Lineup saved");
      setEditMode(false);
      await loadSquads();
    } catch (err) {
      console.error("[MatchLineUp] save failed:", err?.message);
      toast.error("Failed to save lineup");
    } finally {
      setSaving(false);
    }
  };

  // Build lineupData + subs from fetched squads (using each player's
  // user_match_details.lineup_notation: 1 = starting XI, 2 = sub, null = bench).
  const prefillFromSquads = useCallback((home, away) => {
    const data = {};
    const subsList = [];

    const place = (squad, slots, team) => {
      const used = new Set();
      squad
        .filter((p) => p.umd?.lineup_notation === 1)
        .forEach((p) => {
          // Slots sharing this position, ordered top→bottom.
          const samePos = slots
            .filter((s) => s.pos === p.pos)
            .sort((a, b) => a.y - b.y);
          const free = (s) => !used.has(s.id) && !data[s.id];
          // preferred_position_sub_level (0=middle, 1=top, 2=bottom) decides
          // which of the same-position slots this player takes.
          const pick = slotForSubLevel(samePos, p.posSub);
          let slot = pick && free(pick) ? pick : null;
          slot = slot || samePos.find(free) || slots.find(free);
          if (slot) {
            used.add(slot.id);
            data[slot.id] = {
              id: p.id,
              name: p.name,
              num: p.num,
              pos: slot.pos,
              timeIn: p.umd?.start_gametime ?? 0,
              timeOut: p.umd?.end_gametime ?? null,
            };
          }
        });
      squad
        .filter((p) => p.umd?.lineup_notation === 2)
        .forEach((p) => {
          subsList.push({
            id: `sub_${team}_${p.id}`,
            playerId: p.id,
            raw: p.raw,
            team,
            name: p.name,
            num: p.num,
            timeIn: p.umd?.start_gametime ?? 0,
            timeOut: p.umd?.end_gametime ?? null,
            pos: p.pos || posAlias(p.umd?.preferred_position) || null,
            substitutedPlayerId: p.umd?.substituted_player_id ?? null,
            replacesName: p.umd?.substituted_player_name ?? null,
            replacesSlot: null,
          });
        });
    };

    place(home, ALL_HOME_SLOTS, "home");
    place(away, ALL_AWAY_SLOTS, "away");

    // Link each sub to the player they replaced — prefer substituted_player_id,
    // fall back to name. The replaced player may be a placed starter OR another
    // substitute (chained substitutions), so check both lists.
    subsList.forEach((sub) => {
      let refId = null;
      if (sub.substitutedPlayerId != null) {
        refId =
          Object.keys(data).find(
            (id) => String(data[id]?.id) === String(sub.substitutedPlayerId)
          ) ||
          subsList.find(
            (s) => s !== sub && String(s.playerId) === String(sub.substitutedPlayerId)
          )?.id;
      }
      if (!refId && sub.replacesName) {
        refId =
          Object.keys(data).find((id) => data[id]?.name === sub.replacesName) ||
          subsList.find((s) => s !== sub && s.name === sub.replacesName)?.id;
      }
      if (refId) sub.replacesSlot = refId;
    });

    // Snapshot the loaded state — comparing against the raw API fields would
    // flag load-time normalisation (e.g. resolving a null sub-level to a
    // concrete slot) as a user edit.
    const snap = new Map();
    Object.keys(data).forEach((slotId) => {
      const d = data[slotId];
      if (d?.id == null) return;
      snap.set(String(d.id), {
        n: 1,
        slot: slotId,
        pos: null,
        num: d.num,
        tIn: d.timeIn ?? 0,
        tOut: d.timeOut ?? null,
        rep: null,
      });
    });
    subsList.forEach((sb) => {
      if (sb.playerId == null) return;
      snap.set(String(sb.playerId), {
        n: 2,
        slot: null,
        pos: sb.pos ?? null,
        num: sb.num,
        tIn: sb.timeIn ?? 0,
        tOut: sb.timeOut ?? null,
        rep: sb.substitutedPlayerId ?? null,
      });
    });
    loadedRef.current = snap;

    setLineupData(data);
    setSubs(subsList);
  }, []);

  // Stable primitives — `matchInfo` is a new object on every parent render, so
  // depending on it would re-create loadSquads (and re-fire the fetch) needlessly.
  const teamAId = matchInfo?.teamA?.id;
  const teamBId = matchInfo?.teamB?.id;
  const orgId = matchInfo?.organizerId;
  const matchStatus = matchInfo?.status;

  const loadSquads = useCallback(async () => {
    if (!matchId || !token || !teamAId || !teamBId) return null;
    setLoading(true);
    try {
      const playedInMatch = PLAYED_IN_STATUSES.includes(matchStatus);
      const [homeRes, awayRes] = await Promise.all([
        getPlayersRelatedToTeam({ teamId: teamAId, matchId, organizerId: orgId, playedInMatch, token }),
        getPlayersRelatedToTeam({ teamId: teamBId, matchId, organizerId: orgId, playedInMatch, token }),
      ]);
      const home = (homeRes || []).map(mapApiPlayer);
      const away = (awayRes || []).map(mapApiPlayer);
      setHomeSquad(home);
      setAwaySquad(away);
      prefillFromSquads(home, away);
      return { home, away };
    } catch (err) {
      console.error("[MatchLineUp] squad fetch failed:", err?.message);
      toast.error("Failed to load players");
      return null;
    } finally {
      setLoading(false);
    }
  }, [matchId, token, teamAId, teamBId, orgId, matchStatus, prefillFromSquads]);

  // Load squads + existing lineup on mount / when the match changes.
  useEffect(() => {
    loadSquads();
  }, [loadSquads]);

  const fetchLineup = async () => {
    await loadSquads();
    setEditMode(true);
  };

  const getPlacedSlots = (team) => {
    const slots = team === "home" ? ALL_HOME_SLOTS : ALL_AWAY_SLOTS;
    return slots.filter((s) => lineupData[s.id]);
  };

  // A "Replaces Player" reference is either a placed slot id or a sub id
  // (a previously subbed-in player can also be replaced). Resolve to the
  // underlying player { id, name, num, pos }.
  const resolveReplaced = (rid) => {
    if (!rid) return null;
    if (lineupData[rid]) return lineupData[rid];
    const s = subs.find((x) => x.id === rid);
    return s ? { id: s.playerId, name: s.name, num: s.num, pos: s.pos, timeIn: s.timeIn } : null;
  };

  // Options for the "Replaces Player" select: on-field starters + subbed-in
  // players, excluding the sub being edited and anyone already replaced.
  const getReplaceOptions = (team, editIdx) => {
    const takenByOther = (id) =>
      subs.some((s, i) => s.replacesSlot === id && i !== editIdx);
    const placed = getPlacedSlots(team)
      .filter((slot) => !takenByOther(slot.id))
      .map((slot) => {
        const p = lineupData[slot.id];
        return { id: slot.id, playerId: p.id, raw: p.raw, name: p.name, num: p.num, pos: slot.pos };
      });
    const subbedIn = subs
      .filter((s, i) => s.team === team && i !== editIdx && !takenByOther(s.id))
      .map((s) => ({ id: s.id, playerId: s.playerId, raw: s.raw, name: s.name, num: s.num, pos: s.pos }));
    return [...placed, ...subbedIn];
  };

  // Live game_time validation for the open dialogs (blocks Save + shows inline).
  const playerGtErr = playerModal ? gameTimeError(playerForm.timeIn, playerForm.timeOut) : null;
  const subGtErr = subModal ? gameTimeError(subForm.timeIn, subForm.timeOut) : null;

  const homeSubs = subs.filter((s) => s.team === "home");
  const awaySubs = subs.filter((s) => s.team === "away");

  // Ids of players who were substituted OFF — taken from each substitute's
  // user_match_details.substituted_player_id (plus any in-session subs that
  // reference a placed slot). Used to show the ↓ badge on the pitch.
  const substitutedOffIds = new Set();
  // …and who came on for them, for the token tooltip.
  const subInfoByPlayerId = new Map();
  [...homeSquad, ...awaySquad].forEach((p) => {
    const sid = p?.umd?.substituted_player_id;
    if (sid != null && p?.umd?.lineup_notation === 2) {
      substitutedOffIds.add(String(sid));
      subInfoByPlayerId.set(String(sid), {
        name: p.name,
        num: p.num,
        pos: p.pos,
        timeIn: p.umd?.start_gametime ?? null,
      });
    }
  });
  subs.forEach((s) => {
    const rid = resolveReplaced(s.replacesSlot)?.id;
    if (rid != null) {
      substitutedOffIds.add(String(rid));
      subInfoByPlayerId.set(String(rid), {
        name: s.name,
        num: s.num,
        pos: s.pos,
        timeIn: s.timeIn ?? null,
      });
    }
  });

  return (
    <Box sx={{ px: { xs: 1, md: 2 }, py: 2, position: "relative" }}>
      {loading && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 20,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
            backgroundColor: "rgba(0,0,0,0.35)",
            backdropFilter: "blur(1px)",
            borderRadius: "12px",
          }}
        >
          <CircularProgress size={32} sx={{ color: "rgba(232, 108, 0, 1)" }} />
          <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#fff" }}>
            Loading lineup…
          </Typography>
        </Box>
      )}
      {/* Toolbar */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
        sx={{
          mb: 2,
          px: 2,
          py: 1,
          gap: 1,
          backgroundColor: "var(--surface-1)",
          border: "1px solid var(--border-1)",
          borderRadius: "12px",
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="center" flexWrap="wrap" gap={{ xs: 1, sm: 2 }}>
          <Stack direction="row" alignItems="center" gap={1}>
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                backgroundColor: HOME_COLOR,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography sx={{ fontSize: "10px", fontWeight: 700, color: "#fff" }}>
                {matchInfo.teamA.short}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "var(--text-1)" }}>
              {matchInfo.teamA.name}
            </Typography>
            <Chip
              label={`${getPlacedSlots("home").length}/${startersCap}`}
              size="small"
              sx={{
                height: 20,
                fontSize: "11px",
                fontWeight: 600,
                backgroundColor: `${HOME_COLOR}15`,
                color: HOME_COLOR,
              }}
            />
          </Stack>

          <Typography sx={{ fontSize: "12px", color: "var(--text-2)", fontWeight: 600 }}>
            MATCH LINE UP
          </Typography>

          <Stack direction="row" alignItems="center" gap={1}>
            <Chip
              label={`${getPlacedSlots("away").length}/${startersCap}`}
              size="small"
              sx={{
                height: 20,
                fontSize: "11px",
                fontWeight: 600,
                backgroundColor: `${AWAY_COLOR}15`,
                color: AWAY_COLOR,
              }}
            />
            <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "var(--text-1)" }}>
              {matchInfo.teamB.name}
            </Typography>
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                backgroundColor: AWAY_COLOR,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography sx={{ fontSize: "10px", fontWeight: 700, color: "#fff" }}>
                {matchInfo.teamB.short}
              </Typography>
            </Box>
          </Stack>
        </Stack>

        <Stack
          direction="row"
          gap={1}
          justifyContent={{ xs: "stretch", sm: "flex-end" }}
          sx={{ "& .MuiButton-root": { flex: { xs: 1, sm: "none" }, whiteSpace: "nowrap" } }}
        >
          {editMode ? (
            <>
              <Button
                variant="contained"
                size="small"
                startIcon={<Save sx={{ fontSize: "16px !important" }} />}
                onClick={handleSave}
                disabled={saving}
                sx={{
                  textTransform: "none",
                  borderRadius: "8px",
                  fontSize: "12px",
                  fontWeight: 600,
                  backgroundColor: "#10B981",
                  "&:hover": { backgroundColor: "#059669" },
                }}
              >
                {saving ? "Saving…" : "Save"}
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setEditMode(false)}
                sx={{
                  textTransform: "none",
                  borderRadius: "8px",
                  fontSize: "12px",
                  fontWeight: 600,
                  borderColor: "var(--border-1)",
                  color: "var(--text-2)",
                }}
              >
                Cancel
              </Button>
            </>
          ) : (
            <>
              {/* <Button
                variant="contained"
                size="small"
                onClick={fetchLineup}
                disabled={loading}
                sx={{
                  textTransform: "none",
                  borderRadius: "8px",
                  fontSize: "12px",
                  fontWeight: 600,
                  backgroundColor: "rgba(232, 108, 0, 1)",
                  "&:hover": { backgroundColor: "rgba(200, 93, 0, 1)" },
                }}
              >
                {loading ? "Loading…" : "Fetch Lineup"}
              </Button> */}
              {!readOnly && (
                <>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Download sx={{ fontSize: "16px !important" }} />}
                    onClick={() => setLineupModalOpen(true)}
                    sx={{
                      textTransform: "none",
                      borderRadius: "8px",
                      fontSize: "12px",
                      fontWeight: 600,
                      borderColor: "var(--border-1)",
                      color: "var(--text-1)",
                      "&:hover": { backgroundColor: "var(--surface-2)" },
                    }}
                  >
                    Download Lineup
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Edit sx={{ fontSize: "16px !important" }} />}
                    onClick={() => setEditMode(true)}
                    sx={{
                      textTransform: "none",
                      borderRadius: "8px",
                      fontSize: "12px",
                      fontWeight: 600,
                      borderColor: "var(--border-1)",
                      color: "var(--text-1)",
                      "&:hover": { backgroundColor: "var(--surface-2)" },
                    }}
                  >
                    Edit Lineup
                  </Button>
                </>
              )}
            </>
          )}
        </Stack>
      </Stack>

      {/* Main Layout: Squad Panel + Pitch + Squad Panel.
          Side-by-side only on large screens; stacks on mobile/tablet so the
          pitch keeps a usable width. */}
      <Stack
        direction={{ xs: "column", lg: "row" }}
        gap={2}
        alignItems={{ xs: "stretch", lg: "flex-start" }}
      >
        {editMode && (
          <SquadPanel
            team="home"
            squad={homeSquad}
            lineupData={lineupData}
            color={HOME_COLOR}
            onPlayerClick={(p) => handleSquadPlayerClick(p, "home")}
            onCreatePlayer={() => openAddPlayer("home")}
            subNames={homeSubs.map((s) => s.name)}
          />
        )}

        {/* Pitch */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              position: "relative",
              width: "100%",
              // Landscape on desktop/tablet; portrait (vertical) on mobile.
              paddingTop: isMobile ? "178.57%" : "56%",
              borderRadius: "12px",
              overflow: "hidden",
              border: "1px solid var(--border-1)",
            }}
          >
            <Box sx={{ position: "absolute", inset: 0 }}>
              {isMobile ? (
                // Rotate the landscape pitch 90° to fill the portrait container.
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    width: "178.57%",
                    height: "56%",
                    transform: "translate(-50%, -50%) rotate(90deg)",
                  }}
                >
                  <PitchSVG isDark={isDark} />
                </Box>
              ) : (
                <PitchSVG isDark={isDark} />
              )}

              {/* Render all slots */}
              {ALL_SLOTS.map((slot) => (
                <PlayerToken
                  key={slot.id}
                  slot={slot}
                  data={lineupData[slot.id]}
                  color={slot.id.startsWith("h_") ? HOME_COLOR : AWAY_COLOR}
                  editMode={editMode}
                  onClick={() => openSlotModal(slot)}
                  onDrop={(s, e) => handleSlotDrop(s, e)}
                  dragOver={dragOver}
                  setDragOver={setDragOver}
                  isDark={isDark}
                  compact={isMobile}
                  vertical={isMobile}
                  substituted={
                    lineupData[slot.id]?.id != null &&
                    substitutedOffIds.has(String(lineupData[slot.id].id))
                  }
                  subInfo={
                    lineupData[slot.id]?.id != null
                      ? subInfoByPlayerId.get(String(lineupData[slot.id].id)) || null
                      : null
                  }
                />
              ))}
            </Box>
          </Box>

          {/* Substitutions Bench */}
          {(editMode || homeSubs.length > 0 || awaySubs.length > 0) && (
            <Stack
              direction={{ xs: "column", md: "row" }}
              gap={2}
              sx={{ mt: 2 }}
            >
              {/* Home Subs */}
              <Box
                sx={{
                  flex: 1,
                  backgroundColor: "var(--surface-1)",
                  border: "1px solid var(--border-1)",
                  borderRadius: "12px",
                  overflow: "hidden",
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    px: 1.5,
                    py: 1,
                    borderBottom: "1px solid var(--border-1)",
                    backgroundColor: "var(--surface-2)",
                  }}
                >
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Box
                      sx={{
                        width: 22,
                        height: 22,
                        borderRadius: "50%",
                        backgroundColor: HOME_COLOR,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography sx={{ fontSize: "9px", fontWeight: 700, color: "#fff" }}>
                        {matchInfo.teamA.short}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "var(--text-1)" }}>
                      {matchInfo.teamA.name}
                    </Typography>
                    <Typography sx={{ fontSize: "11px", color: "var(--text-2)" }}>
                      Substitutions
                    </Typography>
                  </Stack>
                  {editMode && (
                    <Button
                      size="small"
                      startIcon={<Add sx={{ fontSize: "14px !important" }} />}
                      onClick={() => openSubModal("home")}
                      sx={{
                        textTransform: "none",
                        fontSize: "11px",
                        fontWeight: 600,
                        color: HOME_COLOR,
                      }}
                    >
                      Add Sub
                    </Button>
                  )}
                </Stack>
                {homeSubs.length === 0 ? (
                  <Typography
                    sx={{
                      fontSize: "12px",
                      color: "var(--text-2)",
                      textAlign: "center",
                      py: 2,
                    }}
                  >
                    No substitutions
                  </Typography>
                ) : (
                  homeSubs.map((sub, idx) => {
                    const globalIdx = subs.indexOf(sub);
                    const replaced = resolveReplaced(sub.replacesSlot);
                    return (
                      <Stack
                        key={sub.id}
                        direction="row"
                        alignItems="center"
                        sx={{
                          px: 1.5,
                          py: 0.75,
                          borderBottom: "1px solid var(--border-1)",
                          gap: 1,
                        }}
                      >
                        <Tooltip title={`${sub.name} #${sub.num}${sub.pos ? ` · ${sub.pos}` : ""}`} arrow>
                          <Chip
                            label={
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
                                <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>{sub.name}</Box>
                                <Box component="span" sx={{ flexShrink: 0, whiteSpace: "nowrap" }}>#{sub.num}{sub.pos ? ` · ${sub.pos}` : ""}</Box>
                              </Box>
                            }
                            size="small"
                            sx={{
                              fontSize: "11px",
                              fontWeight: 600,
                              maxWidth: 170,
                              flexShrink: 0,
                              backgroundColor: `${HOME_COLOR}15`,
                              color: HOME_COLOR,
                              "& .MuiChip-label": { overflow: "hidden", width: "100%" },
                            }}
                          />
                        </Tooltip>
                        <Typography sx={{ fontSize: "10px", color: "var(--text-2)" }}>
                          IN {sub.timeIn}'
                        </Typography>
                        {replaced && (
                          <>
                            <SwapHoriz sx={{ fontSize: 14, color: "var(--text-2)", flexShrink: 0 }} />
                            <Tooltip title={`${replaced.name}${replaced.num != null && replaced.num !== "" ? ` #${replaced.num}` : ""}${replaced.pos ? ` · ${replaced.pos}` : ""}`} arrow>
                              <Chip
                                label={
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
                                    <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>{replaced.name}</Box>
                                    <Box component="span" sx={{ flexShrink: 0, whiteSpace: "nowrap" }}>
                                      {replaced.num != null && replaced.num !== "" ? `#${replaced.num}` : ""}{replaced.pos ? `${replaced.num != null && replaced.num !== "" ? " · " : ""}${replaced.pos}` : ""}
                                    </Box>
                                  </Box>
                                }
                                size="small"
                                sx={{
                                  fontSize: "11px",
                                  maxWidth: 170,
                                  flexShrink: 1,
                                  minWidth: 0,
                                  backgroundColor: "#EF444415",
                                  color: "#EF4444",
                                  "& .MuiChip-label": { overflow: "hidden", width: "100%" },
                                }}
                              />
                            </Tooltip>
                          </>
                        )}
                        <Box sx={{ flex: 1 }} />
                        {editMode && (
                          <Stack direction="row" gap={0.5} sx={{ flexShrink: 0 }}>
                            <Tooltip title="Substitute this player out" arrow>
                              <IconButton
                                size="small"
                                onClick={() => openSubModal("home", null, sub.id)}
                                sx={{ width: 24, height: 24 }}
                              >
                                <Add sx={{ fontSize: 15, color: "rgba(232, 108, 0, 1)" }} />
                              </IconButton>
                            </Tooltip>
                            <IconButton
                              size="small"
                              onClick={() =>
                                openSubModal("home", globalIdx)
                              }
                              sx={{ width: 24, height: 24 }}
                            >
                              <Edit sx={{ fontSize: 14, color: "var(--text-2)" }} />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => deleteSub(globalIdx)}
                              sx={{ width: 24, height: 24 }}
                            >
                              <Delete sx={{ fontSize: 14, color: "#EF4444" }} />
                            </IconButton>
                          </Stack>
                        )}
                      </Stack>
                    );
                  })
                )}
              </Box>

              {/* Away Subs */}
              <Box
                sx={{
                  flex: 1,
                  backgroundColor: "var(--surface-1)",
                  border: "1px solid var(--border-1)",
                  borderRadius: "12px",
                  overflow: "hidden",
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    px: 1.5,
                    py: 1,
                    borderBottom: "1px solid var(--border-1)",
                    backgroundColor: "var(--surface-2)",
                  }}
                >
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Box
                      sx={{
                        width: 22,
                        height: 22,
                        borderRadius: "50%",
                        backgroundColor: AWAY_COLOR,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography sx={{ fontSize: "9px", fontWeight: 700, color: "#fff" }}>
                        {matchInfo.teamB.short}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "var(--text-1)" }}>
                      {matchInfo.teamB.name}
                    </Typography>
                    <Typography sx={{ fontSize: "11px", color: "var(--text-2)" }}>
                      Substitutions
                    </Typography>
                  </Stack>
                  {editMode && (
                    <Button
                      size="small"
                      startIcon={<Add sx={{ fontSize: "14px !important" }} />}
                      onClick={() => openSubModal("away")}
                      sx={{
                        textTransform: "none",
                        fontSize: "11px",
                        fontWeight: 600,
                        color: AWAY_COLOR,
                      }}
                    >
                      Add Sub
                    </Button>
                  )}
                </Stack>
                {awaySubs.length === 0 ? (
                  <Typography
                    sx={{
                      fontSize: "12px",
                      color: "var(--text-2)",
                      textAlign: "center",
                      py: 2,
                    }}
                  >
                    No substitutions
                  </Typography>
                ) : (
                  awaySubs.map((sub, idx) => {
                    const globalIdx = subs.indexOf(sub);
                    const replaced = resolveReplaced(sub.replacesSlot);
                    return (
                      <Stack
                        key={sub.id}
                        direction="row"
                        alignItems="center"
                        sx={{
                          px: 1.5,
                          py: 0.75,
                          borderBottom: "1px solid var(--border-1)",
                          gap: 1,
                        }}
                      >
                        <Tooltip title={`${sub.name} #${sub.num}${sub.pos ? ` · ${sub.pos}` : ""}`} arrow>
                          <Chip
                            label={
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
                                <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>{sub.name}</Box>
                                <Box component="span" sx={{ flexShrink: 0, whiteSpace: "nowrap" }}>#{sub.num}{sub.pos ? ` · ${sub.pos}` : ""}</Box>
                              </Box>
                            }
                            size="small"
                            sx={{
                              fontSize: "11px",
                              fontWeight: 600,
                              maxWidth: 170,
                              flexShrink: 0,
                              backgroundColor: `${AWAY_COLOR}15`,
                              color: AWAY_COLOR,
                              "& .MuiChip-label": { overflow: "hidden", width: "100%" },
                            }}
                          />
                        </Tooltip>
                        <Typography sx={{ fontSize: "10px", color: "var(--text-2)" }}>
                          IN {sub.timeIn}'
                        </Typography>
                        {replaced && (
                          <>
                            <SwapHoriz sx={{ fontSize: 14, color: "var(--text-2)", flexShrink: 0 }} />
                            <Tooltip title={`${replaced.name}${replaced.num != null && replaced.num !== "" ? ` #${replaced.num}` : ""}${replaced.pos ? ` · ${replaced.pos}` : ""}`} arrow>
                              <Chip
                                label={
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
                                    <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>{replaced.name}</Box>
                                    <Box component="span" sx={{ flexShrink: 0, whiteSpace: "nowrap" }}>
                                      {replaced.num != null && replaced.num !== "" ? `#${replaced.num}` : ""}{replaced.pos ? `${replaced.num != null && replaced.num !== "" ? " · " : ""}${replaced.pos}` : ""}
                                    </Box>
                                  </Box>
                                }
                                size="small"
                                sx={{
                                  fontSize: "11px",
                                  maxWidth: 170,
                                  flexShrink: 1,
                                  minWidth: 0,
                                  backgroundColor: "#EF444415",
                                  color: "#EF4444",
                                  "& .MuiChip-label": { overflow: "hidden", width: "100%" },
                                }}
                              />
                            </Tooltip>
                          </>
                        )}
                        <Box sx={{ flex: 1 }} />
                        {editMode && (
                          <Stack direction="row" gap={0.5} sx={{ flexShrink: 0 }}>
                            <Tooltip title="Substitute this player out" arrow>
                              <IconButton
                                size="small"
                                onClick={() => openSubModal("away", null, sub.id)}
                                sx={{ width: 24, height: 24 }}
                              >
                                <Add sx={{ fontSize: 15, color: "rgba(232, 108, 0, 1)" }} />
                              </IconButton>
                            </Tooltip>
                            <IconButton
                              size="small"
                              onClick={() =>
                                openSubModal("away", globalIdx)
                              }
                              sx={{ width: 24, height: 24 }}
                            >
                              <Edit sx={{ fontSize: 14, color: "var(--text-2)" }} />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => deleteSub(globalIdx)}
                              sx={{ width: 24, height: 24 }}
                            >
                              <Delete sx={{ fontSize: 14, color: "#EF4444" }} />
                            </IconButton>
                          </Stack>
                        )}
                      </Stack>
                    );
                  })
                )}
              </Box>
            </Stack>
          )}
        </Box>

        {editMode && (
          <SquadPanel
            team="away"
            squad={awaySquad}
            lineupData={lineupData}
            color={AWAY_COLOR}
            onPlayerClick={(p) => handleSquadPlayerClick(p, "away")}
            onCreatePlayer={() => openAddPlayer("away")}
            subNames={awaySubs.map((s) => s.name)}
          />
        )}
      </Stack>

      {/* ─── Player Modal ─── */}
      <Dialog
        open={!!playerModal}
        onClose={() => setPlayerModal(null)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: "12px",
            minWidth: { sm: "340px" },
            m: { xs: 1.5, sm: "auto" },
            border: "1px solid var(--border-1)",
          },
        }}
      >
        {playerModal && (
          <>
            <DialogTitle
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                py: 1.5,
              }}
            >
              <Stack direction="row" alignItems="center" gap={1}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor:
                      playerModal.team === "home" ? HOME_COLOR : AWAY_COLOR,
                  }}
                />
                <Typography sx={{ fontSize: "15px", fontWeight: 600, color: "var(--text-1)" }}>
                  {playerModal.slotId && lineupData[playerModal.slotId] ? "Edit" : "Add"} Player
                  {playerModal.pos ? ` — ${playerModal.pos}` : ""}
                </Typography>
              </Stack>
              <IconButton
                size="small"
                onClick={() => setPlayerModal(null)}
                sx={{ color: "var(--text-2)" }}
              >
                <Close fontSize="small" />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: "16px !important", pb: 1, display: "flex", flexDirection: "column", gap: 2 }}>
              {/* Bring in a player who isn't in the squad yet — global search + create. */}
              <Button
                size="small"
                startIcon={<PersonAdd sx={{ fontSize: "14px !important" }} />}
                onClick={() => { const { team, slotId } = playerModal; setPlayerModal(null); openAddPlayer(team, slotId); }}
                sx={{ alignSelf: "flex-start", textTransform: "none", fontSize: "12px", fontWeight: 600, color: "rgba(232, 108, 0, 1)", px: 0 }}
              >
                Search globally / create new player
              </Button>
              {playerModal.pickPosition && (() => {
                const teamSlots = playerModal.team === "home" ? ALL_HOME_SLOTS : ALL_AWAY_SLOTS;
                const emptySlots = teamSlots.filter((s) => !lineupData[s.id]);
                const positionOptions = [...new Set(emptySlots.map((s) => s.pos))].map((pos) => ({
                  value: pos,
                  label: pos,
                }));
                const slotsForPos = emptySlots.filter(
                  (s) => s.pos === (playerModal.selectedPos || "")
                );
                return (
                  <CustomSelect
                    label="Position"
                    required
                    value={playerModal.selectedPos || ""}
                    onChange={(v) => {
                      const slot = emptySlots.find((s) => s.pos === v);
                      setPlayerModal((prev) => ({
                        ...prev,
                        selectedPos: v,
                        selectedSlotId: slot ? slot.id : null,
                      }));
                    }}
                    placeholder="Select position"
                    options={positionOptions}
                  />
                );
              })()}
              {(() => {
                const squad = playerModal.team === "home" ? homeSquad : awaySquad;
                const placedNames = Object.entries(lineupData)
                  .filter(([id, d]) => d && id !== playerModal.slotId)
                  .map(([, d]) => d.name);
                const availablePlayers = squad.filter((p) => !placedNames.includes(p.name));
                const selectedPlayer = availablePlayers.find((p) => p.name === playerForm.name) || null;
                return (
                  <LabeledField label="Player Name" required>
                    <Autocomplete
                      size="small"
                      fullWidth
                      value={selectedPlayer}
                      onChange={(_, newVal) => {
                        if (newVal) {
                          setPlayerForm((f) => ({ ...f, id: newVal.id ?? null, name: newVal.name, num: String(newVal.num) }));
                        } else {
                          setPlayerForm((f) => ({ ...f, id: null, name: "", num: "" }));
                        }
                      }}
                      options={availablePlayers}
                      getOptionLabel={(opt) => opt.name || ""}
                      isOptionEqualToValue={(opt, val) => opt.name === val.name}
                      renderOption={(props, opt) => (
                        <Box component="li" {...props} key={opt.id} sx={{ display: "flex", alignItems: "center", gap: 1.5, py: "8px !important", px: "12px !important", borderBottom: "1px solid var(--border-1)", "&:last-child": { borderBottom: "none" } }}>
                          <Box sx={{ width: 30, height: 30, borderRadius: "50%", border: `2px solid ${playerModal.team === "home" ? HOME_COLOR : AWAY_COLOR}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <Typography sx={{ fontSize: "10px", fontWeight: 700, color: playerModal.team === "home" ? HOME_COLOR : AWAY_COLOR }}>{getInitials(opt.name)}</Typography>
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontSize: "13px", fontWeight: 500, color: "var(--text-1)" }}>{opt.name}</Typography>
                            <PlayerMetaLine items={squadPlayerMeta(opt)} />
                          </Box>
                        </Box>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Search player..."
                          InputProps={{ ...params.InputProps, sx: { borderRadius: "8px", fontSize: "13px", backgroundColor: "var(--surface-2)", "& fieldset": { borderColor: "var(--border-1)" } } }}
                        />
                      )}
                      slotProps={{ paper: { sx: { borderRadius: "10px", border: "1px solid var(--border-1)", boxShadow: "var(--shadow-1)", mt: 0.5, "& .MuiAutocomplete-listbox": { py: 0, maxHeight: "240px" } } } }}
                      noOptionsText={
                        <Stack alignItems="center" gap={1} sx={{ py: 1 }}>
                          <Typography sx={{ fontSize: "13px", color: "var(--text-2)" }}>No players found</Typography>
                          <Button size="small" startIcon={<PersonAdd sx={{ fontSize: "14px !important" }} />} onClick={() => { const { team, slotId } = playerModal; setPlayerModal(null); openAddPlayer(team, slotId); }} sx={{ textTransform: "none", fontSize: "12px", fontWeight: 600, borderRadius: "8px", color: "rgba(232, 108, 0, 1)" }}>
                            Search &amp; Add Player
                          </Button>
                        </Stack>
                      }
                    />
                  </LabeledField>
                );
              })()}
              <LabeledField label="Jersey Number" required>
                <TextField size="small" fullWidth type="number" placeholder="e.g. 10" value={playerForm.num} onChange={(e) => setPlayerForm((f) => ({ ...f, num: e.target.value }))} sx={fieldSx} />
              </LabeledField>
              <Stack direction="row" gap={1.5}>
                <LabeledField label="Time In (min)">
                  <TextField size="small" fullWidth type="number" inputProps={{ min: 0 }} placeholder="0" value={playerForm.timeIn} onChange={(e) => setPlayerForm((f) => ({ ...f, timeIn: e.target.value }))} error={!!playerGtErr} sx={fieldSx} />
                </LabeledField>
                <LabeledField label="Time Out (min)">
                  <TextField size="small" fullWidth type="number" inputProps={{ min: 0 }} placeholder="Optional" value={playerForm.timeOut} onChange={(e) => setPlayerForm((f) => ({ ...f, timeOut: e.target.value }))} error={!!playerGtErr} sx={fieldSx} />
                </LabeledField>
              </Stack>
              {playerGtErr && (
                <Typography sx={{ fontSize: "11px", fontWeight: 500, color: "#d32f2f", mt: -0.5 }}>
                  {playerGtErr}
                </Typography>
              )}
              <LabeledField label="Total Game Time (min)">
                <TextField
                  size="small"
                  fullWidth
                  type="number"
                  inputProps={{ min: 1 }}
                  placeholder="—"
                  error={!!playerGtErr}
                  value={
                    playerForm.timeOut !== "" && playerForm.timeOut != null
                      ? Math.max(
                          0,
                          (parseInt(playerForm.timeOut) || 0) -
                            (parseInt(playerForm.timeIn) || 0)
                        )
                      : ""
                  }
                  onChange={(e) => {
                    const total = e.target.value;
                    setPlayerForm((f) => ({
                      ...f,
                      timeOut:
                        total === ""
                          ? ""
                          : String(
                              (parseInt(f.timeIn) || 0) + (parseInt(total) || 0)
                            ),
                    }));
                  }}
                  sx={fieldSx}
                />
              </LabeledField>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
              {lineupData[playerModal.slotId] &&
                !subs.some((sb) => sb.replacesSlot === playerModal.slotId) && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      const slotId = playerModal.slotId;
                      const team = playerModal.team;
                      setPlayerModal(null);
                      openSubModal(team, null, slotId);
                    }}
                    startIcon={<SwapHoriz sx={{ fontSize: "16px !important" }} />}
                    sx={{
                      textTransform: "none",
                      borderRadius: "8px",
                      fontSize: "12px",
                      borderColor: "rgba(232, 108, 0, 0.5)",
                      color: "rgba(232, 108, 0, 1)",
                    }}
                  >
                    Substitute
                  </Button>
                )}
              {lineupData[playerModal.slotId] && (
                <Button
                  variant="outlined"
                  size="small"
                  color="error"
                  onClick={removePlayer}
                  startIcon={<Delete sx={{ fontSize: "14px !important" }} />}
                  sx={{
                    textTransform: "none",
                    borderRadius: "8px",
                    fontSize: "12px",
                    mr: "auto",
                  }}
                >
                  Remove
                </Button>
              )}
              <Button
                variant="outlined"
                size="small"
                onClick={() => setPlayerModal(null)}
                sx={{
                  textTransform: "none",
                  borderRadius: "8px",
                  fontSize: "12px",
                  borderColor: "var(--border-1)",
                  color: "var(--text-2)",
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={savePlayer}
                disabled={!playerForm.name || !playerForm.num || !!playerGtErr || (playerModal.pickPosition && !playerModal.selectedSlotId)}
                sx={{
                  textTransform: "none",
                  borderRadius: "8px",
                  fontSize: "12px",
                  fontWeight: 600,
                  backgroundColor: "rgba(232, 108, 0, 1)",
                  "&:hover": { backgroundColor: "rgba(200, 93, 0, 1)" },
                }}
              >
                {lineupData[playerModal.slotId] ? "Update" : "Place"}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ─── Substitution Modal ─── */}
      <Dialog
        open={!!subModal}
        onClose={() => setSubModal(null)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: "12px",
            minWidth: { sm: "340px" },
            m: { xs: 1.5, sm: "auto" },
            border: "1px solid var(--border-1)",
          },
        }}
      >
        {subModal && (
          <>
            <DialogTitle
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                py: 1.5,
              }}
            >
              <Stack direction="row" alignItems="center" gap={1}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor:
                      subModal.team === "home" ? HOME_COLOR : AWAY_COLOR,
                  }}
                />
                <Typography sx={{ fontSize: "15px", fontWeight: 600, color: "var(--text-1)" }}>
                  {subModal.editIdx != null ? "Edit" : "Add"} Substitution
                </Typography>
              </Stack>
              <IconButton
                size="small"
                onClick={() => setSubModal(null)}
                sx={{ color: "var(--text-2)" }}
              >
                <Close fontSize="small" />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: "16px !important", pb: 1, display: "flex", flexDirection: "column", gap: 2 }}>
              {(() => {
                const squad = subModal.team === "home" ? homeSquad : awaySquad;
                const placedNames = Object.values(lineupData).filter(Boolean).map((d) => d.name);
                const subNames = subs.filter((_, i) => i !== subModal.editIdx).map((s) => s.name);
                const usedNames = [...placedNames, ...subNames];
                const availablePlayers = squad.filter((p) => !usedNames.includes(p.name));
                const selectedPlayer = availablePlayers.find((p) => p.name === subForm.name) || null;
                return (
                  <LabeledField label="Sub Player Name" required>
                    <Autocomplete
                      size="small"
                      fullWidth
                      value={selectedPlayer}
                      onChange={(_, newVal) => {
                        if (newVal) {
                          setSubForm((f) => ({ ...f, id: newVal.id ?? null, name: newVal.name, num: String(newVal.num) }));
                        } else {
                          setSubForm((f) => ({ ...f, id: null, name: "", num: "" }));
                        }
                      }}
                      options={availablePlayers}
                      getOptionLabel={(opt) => opt.name || ""}
                      isOptionEqualToValue={(opt, val) => opt.name === val.name}
                      renderOption={(props, opt) => (
                        <Box component="li" {...props} key={opt.id} sx={{ display: "flex", alignItems: "center", gap: 1.5, py: "8px !important", px: "12px !important", borderBottom: "1px solid var(--border-1)", "&:last-child": { borderBottom: "none" } }}>
                          <Box sx={{ width: 28, height: 28, borderRadius: "50%", border: `2px solid ${subModal.team === "home" ? HOME_COLOR : AWAY_COLOR}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <Typography sx={{ fontSize: "9px", fontWeight: 700, color: subModal.team === "home" ? HOME_COLOR : AWAY_COLOR }}>{getInitials(opt.name)}</Typography>
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontSize: "13px", fontWeight: 500, color: "var(--text-1)" }}>{opt.name}</Typography>
                            <PlayerMetaLine items={squadPlayerMeta(opt)} />
                          </Box>
                        </Box>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Search player..."
                          InputProps={{ ...params.InputProps, sx: { borderRadius: "8px", fontSize: "13px", backgroundColor: "var(--surface-2)", "& fieldset": { borderColor: "var(--border-1)" } } }}
                        />
                      )}
                      slotProps={{ paper: { sx: { borderRadius: "10px", border: "1px solid var(--border-1)", boxShadow: "var(--shadow-1)", mt: 0.5, "& .MuiAutocomplete-listbox": { py: 0, maxHeight: "200px" } } } }}
                      noOptionsText={
                        <Stack alignItems="center" gap={1} sx={{ py: 1 }}>
                          <Typography sx={{ fontSize: "13px", color: "var(--text-2)" }}>No players found</Typography>
                          <Button size="small" startIcon={<PersonAdd sx={{ fontSize: "14px !important" }} />} onClick={() => { setSubModal(null); openAddPlayer(subModal.team); }} sx={{ textTransform: "none", fontSize: "12px", fontWeight: 600, borderRadius: "8px", color: "rgba(232, 108, 0, 1)" }}>
                            Search &amp; Add Player
                          </Button>
                        </Stack>
                      }
                    />
                  </LabeledField>
                );
              })()}
              <Stack direction="row" gap={1.5}>
                <LabeledField label="Jersey Number" required>
                  <TextField size="small" fullWidth type="number" placeholder="e.g. 10" value={subForm.num} onChange={(e) => setSubForm((f) => ({ ...f, num: e.target.value }))} sx={fieldSx} />
                </LabeledField>
                <LabeledField label="Time In (min)" required>
                  <TextField size="small" fullWidth type="number" inputProps={{ min: 0 }} placeholder="e.g. 65" value={subForm.timeIn} onChange={(e) => setSubForm((f) => ({ ...f, timeIn: e.target.value }))} error={!!subGtErr} sx={fieldSx} />
                </LabeledField>
                <LabeledField label="Time Out (min)">
                  <TextField size="small" fullWidth type="number" inputProps={{ min: 0 }} placeholder={`e.g. ${matchGameTime}`} value={subForm.timeOut} onChange={(e) => setSubForm((f) => ({ ...f, timeOut: e.target.value }))} error={!!subGtErr} sx={fieldSx} />
                </LabeledField>
              </Stack>
              {subGtErr && (
                <Typography sx={{ fontSize: "11px", fontWeight: 500, color: "#d32f2f", mt: -0.5 }}>
                  {subGtErr}
                </Typography>
              )}
              {(() => {
                // On-field starters + subbed-in players (a subbed-in player can
                // also be replaced), excluding anyone already replaced.
                const replaceOptions = getReplaceOptions(subModal.team, subModal.editIdx);
                const selectedReplace = replaceOptions.find((o) => o.id === subForm.replacesSlot) || null;
                return (
                  <LabeledField label="Replaces Player (substituted out)" required>
                    <Autocomplete
                      size="small"
                      fullWidth
                      value={selectedReplace}
                      onChange={(_, newVal) =>
                        setSubForm((f) => ({
                          ...f,
                          replacesSlot: newVal?.id || "",
                          // Default the position to the replaced player's; still editable below.
                          pos: newVal?.pos || f.pos,
                        }))
                      }
                      options={replaceOptions}
                      getOptionLabel={(o) => (o ? `#${o.num} ${o.name} — ${o.pos}` : "")}
                      isOptionEqualToValue={(o, v) => o.id === v.id}
                      renderOption={(props, opt) => (
                        <Box component="li" {...props} key={opt.id} sx={{ display: "flex", alignItems: "center", gap: 1.5, py: "8px !important", px: "12px !important", borderBottom: "1px solid var(--border-1)", "&:last-child": { borderBottom: "none" } }}>
                          <Box sx={{ width: 28, height: 28, borderRadius: "50%", border: `2px solid ${subModal.team === "home" ? HOME_COLOR : AWAY_COLOR}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <Typography sx={{ fontSize: "9px", fontWeight: 700, color: subModal.team === "home" ? HOME_COLOR : AWAY_COLOR }}>{getInitials(opt.name)}</Typography>
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontSize: "13px", fontWeight: 500, color: "var(--text-1)" }}>{opt.name}</Typography>
                            <PlayerMetaLine items={squadPlayerMeta(opt)} />
                          </Box>
                        </Box>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Search player..."
                          InputProps={{ ...params.InputProps, sx: { borderRadius: "8px", fontSize: "13px", backgroundColor: "var(--surface-2)", "& fieldset": { borderColor: "var(--border-1)" } } }}
                        />
                      )}
                      slotProps={{ paper: { sx: { borderRadius: "10px", border: "1px solid var(--border-1)", boxShadow: "var(--shadow-1)", mt: 0.5, "& .MuiAutocomplete-listbox": { py: 0, maxHeight: "200px" } } } }}
                    />
                  </LabeledField>
                );
              })()}
              <CustomSelect
                label="Position"
                value={subForm.pos}
                onChange={(v) => setSubForm((f) => ({ ...f, pos: v }))}
                placeholder="Select position"
                options={[
                  { value: "", label: "None" },
                  ...POSITIONS.map((p) => ({ value: p, label: p })),
                ]}
              />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setSubModal(null)}
                sx={{
                  textTransform: "none",
                  borderRadius: "8px",
                  fontSize: "12px",
                  borderColor: "var(--border-1)",
                  color: "var(--text-2)",
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={saveSub}
                disabled={!subForm.name || !subForm.num || !subForm.timeIn || !!subGtErr || !subForm.replacesSlot}
                sx={{
                  textTransform: "none",
                  borderRadius: "8px",
                  fontSize: "12px",
                  fontWeight: 600,
                  backgroundColor: "rgba(232, 108, 0, 1)",
                  "&:hover": { backgroundColor: "rgba(200, 93, 0, 1)" },
                }}
              >
                {subModal.editIdx != null ? "Update" : "Add Sub"}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ─── Create Player Modal ─── */}
      <Dialog
        open={!!createPlayerModal}
        onClose={closeCreatePlayer}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            border: "1px solid var(--border-1)",
          },
        }}
      >
        {createPlayerModal && (
          <>
            <DialogTitle
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                py: 1.5,
              }}
            >
              <Stack direction="row" alignItems="center" gap={1}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor:
                      createPlayerModal.team === "home"
                        ? HOME_COLOR
                        : AWAY_COLOR,
                  }}
                />
                <Typography
                  sx={{
                    fontSize: "16px",
                    fontWeight: 600,
                    color: "var(--text-1)",
                  }}
                >
                  Add New Player
                </Typography>
                <Chip
                  label={
                    createPlayerModal.team === "home"
                      ? matchInfo.teamA.name
                      : matchInfo.teamB.name
                  }
                  size="small"
                  sx={{
                    fontSize: "11px",
                    height: 22,
                    fontWeight: 600,
                    backgroundColor:
                      createPlayerModal.team === "home"
                        ? `${HOME_COLOR}15`
                        : `${AWAY_COLOR}15`,
                    color:
                      createPlayerModal.team === "home"
                        ? HOME_COLOR
                        : AWAY_COLOR,
                  }}
                />
              </Stack>
              <IconButton
                size="small"
                onClick={closeCreatePlayer}
                sx={{ color: "var(--text-2)" }}
              >
                <Close fontSize="small" />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: "16px !important", pb: 1, display: "flex", flexDirection: "column", gap: 2 }}>
              {createDup ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Typography sx={{ fontSize: "13px", color: "var(--text-2)" }}>
                    {createDup.message ||
                      "Matching players already exist. Select the one that is the same player, Port to Team when they are already in this organizer, or Port to Organizer when they belong to another one — or ask an Admin to approve a brand-new player."}
                  </Typography>
                  <Box sx={{ maxHeight: 320, overflowY: "auto" }}>
                    {createMatches.map((p) => {
                      const color = createPlayerModal.team === "home" ? HOME_COLOR : AWAY_COLOR;
                      const { action, label, hint, teamNames, organizerNames } = playerAddState(p, createPlayerModal.team);
                      return (
                        <Stack key={p.id} direction="row" alignItems="center" gap={1.5} sx={{ px: 1, py: 1, borderBottom: "1px solid var(--border-1)" }}>
                          <Box sx={{ width: 32, height: 32, borderRadius: "50%", border: `2px solid ${color}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <Typography sx={{ fontSize: "10px", fontWeight: 700, color }}>{getInitials(p.name)}</Typography>
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontSize: "13px", fontWeight: 500, color: "var(--text-1)" }}>{p.name}</Typography>
                            <PlayerMetaLine
                              items={[
                                { label: "Player ID", value: p.id },
                                {
                                  label: teamNames.length > 1 ? "Team Names" : "Team Name",
                                  value: teamNames.join(", "),
                                },
                                {
                                  label: organizerNames.length > 1 ? "Organizer Names" : "Organizer Name",
                                  value: organizerNames.join(", "),
                                },
                                { label: "Jersey Number", value: p.jersey },
                                { label: "Position", value: p.position },
                                { label: "Name Match", value: p.fuzz != null ? `${p.fuzz}%` : null },
                              ]}
                            />
                            <Typography sx={{ fontSize: "10px", color: "var(--text-2)", fontStyle: "italic", mt: 0.25 }}>
                              {hint}
                            </Typography>
                          </Box>
                          <Button variant="outlined" size="small" startIcon={<PersonAdd sx={{ fontSize: "14px !important" }} />} onClick={() => (action === "SELECT" ? selectMatchedPlayer(p) : portMatchedPlayer(p))} disabled={createBusy} sx={{ textTransform: "none", borderRadius: "8px", fontSize: "11px", fontWeight: 600, borderColor: color, color, whiteSpace: "normal", lineHeight: 1.2, flexShrink: 0, maxWidth: 130, textAlign: "center" }}>
                            {label}
                          </Button>
                        </Stack>
                      );
                    })}
                  </Box>
                </Box>
              ) : (
              <>
              {createError && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1,
                    px: 1.5,
                    py: 1,
                    borderRadius: "8px",
                    backgroundColor: "rgba(239, 68, 68, 0.10)",
                    border: "1px solid rgba(239, 68, 68, 0.35)",
                  }}
                >
                  <Typography sx={{ fontSize: "13px", fontWeight: 500, color: "#EF4444", lineHeight: 1.4 }}>
                    {createError}
                  </Typography>
                </Box>
              )}
              <PlayerProfileForm value={createForm} onChange={setCreateForm} />
              </>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
              {createDup ? (
                <>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => { setCreateDup(null); setCreateError(null); }}
                    disabled={createBusy}
                    sx={{ textTransform: "none", borderRadius: "8px", fontSize: "13px", borderColor: "var(--border-1)", color: "var(--text-2)", px: 3, mr: "auto" }}
                  >
                    Back
                  </Button>
                  {createDup.duplicateType === DUPLICATE_TYPE.SAME_COUNTRY ? (
                    <Button
                      variant="contained"
                      size="small"
                      onClick={overrideCreateViaFlag}
                      disabled={createBusy}
                      sx={{ textTransform: "none", borderRadius: "8px", fontSize: "13px", fontWeight: 600, px: 3, backgroundColor: "rgba(232, 108, 0, 1)", "&:hover": { backgroundColor: "rgba(200, 93, 0, 1)" } }}
                    >
                      {createBusy ? "…" : "Raise Flag"}
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      size="small"
                      onClick={raiseApprovalRequest}
                      disabled={createBusy}
                      sx={{ textTransform: "none", borderRadius: "8px", fontSize: "13px", fontWeight: 600, px: 3, backgroundColor: "rgba(232, 108, 0, 1)", "&:hover": { backgroundColor: "rgba(200, 93, 0, 1)" } }}
                    >
                      {createBusy ? "…" : "Raise Request to Admin"}
                    </Button>
                  )}
                </>
              ) : (
                <>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => { closeCreatePlayer(); setPendingSlot(null); }}
                    sx={{ textTransform: "none", borderRadius: "8px", fontSize: "13px", borderColor: "var(--border-1)", color: "var(--text-2)", px: 3 }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleCreatePlayer}
                    disabled={createBusy || !createForm.firstName}
                    sx={{
                      textTransform: "none",
                      borderRadius: "8px",
                      fontSize: "13px",
                      fontWeight: 600,
                      px: 4,
                      backgroundColor: "rgba(232, 108, 0, 1)",
                      "&:hover": { backgroundColor: "rgba(200, 93, 0, 1)" },
                    }}
                  >
                    {createBusy ? "…" : createForm.isDummy ? "Create Dummy" : "Add Player"}
                  </Button>
                </>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ─── Search & Add Player Modal (global search → add to team) ─── */}
      <Dialog
        open={!!addModal}
        onClose={() => { setAddModal(null); setPendingSlot(null); }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px", border: "1px solid var(--border-1)" } }}
      >
        {addModal && (
          <>
            <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 1.5 }}>
              <Stack direction="row" alignItems="center" gap={1}>
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: addModal.team === "home" ? HOME_COLOR : AWAY_COLOR }} />
                <Typography sx={{ fontSize: "15px", fontWeight: 600, color: "var(--text-1)" }}>
                  Add Player —{" "}
                  {addModal.team === "home" ? matchInfo?.teamA?.name : matchInfo?.teamB?.name}
                </Typography>
              </Stack>
              <IconButton size="small" onClick={() => setAddModal(null)} sx={{ color: "var(--text-2)" }}>
                <Close fontSize="small" />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: "16px !important", pb: 1 }}>
              <Stack direction="row" gap={1}>
                <TextField
                  size="small"
                  fullWidth
                  autoFocus
                  placeholder="Search player by name…"
                  value={playerSearch}
                  onChange={(e) => setPlayerSearch(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") runPlayerSearch(playerSearch); }}
                  sx={fieldSx}
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => runPlayerSearch(playerSearch)}
                  disabled={searching || !playerSearch.trim()}
                  sx={{ textTransform: "none", borderRadius: "8px", fontSize: "12px", fontWeight: 600, backgroundColor: "rgba(232, 108, 0, 1)", "&:hover": { backgroundColor: "rgba(200, 93, 0, 1)" } }}
                >
                  {searching ? "…" : "Search"}
                </Button>
              </Stack>

              <Box sx={{ mt: 1.5, maxHeight: 320, overflowY: "auto" }}>
                {searchMatches.length === 0 ? (
                  <Typography sx={{ fontSize: "13px", color: "var(--text-2)", textAlign: "center", py: 3 }}>
                    {searching
                      ? "Searching…"
                      : /* Spec §5 — too many matches, refine the search. */
                        searchDup?.message ||
                        "Search for a player to add — or create a new one below."}
                  </Typography>
                ) : (
                  searchMatches.map((p) => {
                    const color = addModal.team === "home" ? HOME_COLOR : AWAY_COLOR;
                    const { label: actionLabel, hint, teamNames, organizerNames } = playerAddState(p, addModal.team);
                    const label = addingId === p.id ? "Adding…" : actionLabel;
                    return (
                      <Stack key={p.id} direction="row" alignItems="center" gap={1.5} sx={{ px: 1, py: 1, borderBottom: "1px solid var(--border-1)" }}>
                        <Box sx={{ width: 32, height: 32, borderRadius: "50%", border: `2px solid ${color}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          <Typography sx={{ fontSize: "10px", fontWeight: 700, color }}>{getInitials(p.name)}</Typography>
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: "13px", fontWeight: 500, color: "var(--text-1)" }}>{p.name}</Typography>
                          <PlayerMetaLine
                            items={[
                              { label: "Player ID", value: p.id },
                              { label: "Position", value: p.position },
                              {
                                label: teamNames.length > 1 ? "Team Names" : "Team Name",
                                value: teamNames.join(", "),
                              },
                              {
                                label: organizerNames.length > 1 ? "Organizer Names" : "Organizer Name",
                                value: organizerNames.join(", "),
                              },
                              { label: "Jersey Number", value: p.jersey },
                              { label: "Name Match", value: p.fuzz != null ? `${p.fuzz}%` : null },
                            ]}
                          />
                          <Typography sx={{ fontSize: "10px", color: "var(--text-2)", fontStyle: "italic", mt: 0.25 }}>
                            {hint}
                          </Typography>
                        </Box>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<PersonAdd sx={{ fontSize: "14px !important" }} />}
                          onClick={() => addSearchedPlayer(p)}
                          disabled={addingId === p.id}
                          sx={{ textTransform: "none", borderRadius: "8px", fontSize: "11px", fontWeight: 600, borderColor: color, color, whiteSpace: "normal", lineHeight: 1.2, flexShrink: 0, maxWidth: 130, textAlign: "center" }}
                        >
                          {label}
                        </Button>
                      </Stack>
                    );
                  })
                )}
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => { setAddModal(null); setPendingSlot(null); }}
                sx={{ textTransform: "none", borderRadius: "8px", fontSize: "12px", borderColor: "var(--border-1)", color: "var(--text-2)", mr: "auto" }}
              >
                Close
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={<PersonAdd sx={{ fontSize: "14px !important" }} />}
                onClick={openCreateFromSearch}
                sx={{ textTransform: "none", borderRadius: "8px", fontSize: "12px", fontWeight: 600, backgroundColor: "rgba(232, 108, 0, 1)", "&:hover": { backgroundColor: "rgba(200, 93, 0, 1)" } }}
              >
                Create New Player
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <LineupDownloadModal
        open={lineupModalOpen}
        onClose={() => setLineupModalOpen(false)}
        pdfDetails={matchInfo?.pdfDetails}
        teamAName={matchInfo?.teamA?.name}
        teamBName={matchInfo?.teamB?.name}
      />
    </Box>
  );
};

export default MatchLineUp;
